/**
 * COP Dashboard - Simplified Clean Layout
 * Following Pathfinder pattern for optimal user experience
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Autocomplete,
  TextField,
  useMediaQuery,
  useTheme,
  Collapse,
  Tooltip,
  SelectChangeEvent,
  Badge,
  Snackbar,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  LocationOn as GeofenceIcon,
  AccessTime as DwellIcon,
  Map as MapIcon,
  DeviceHub as StatusIcon,
  ReportProblem as AlertIcon,
  FilterList as FilterIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  BarChart,
  CheckCircle,
  Bolt,
  School,
  Person,
  Shield,
  LocationOn,
  Sync,
  SettingsInputAntenna,
  Chat as ChatIcon,
  PhoneIphone,
  Watch as WatchIcon,
  DirectionsCar,
  Videocam as VideocamIcon,
  Layers as LayersIcon,
  Videocam as CameraGlyphIcon,
} from '@mui/icons-material';
import { DeviceLocationStatus, copApiService } from '../services/copApi';
import { copDataService } from '../services/copDataService';
import { copDwellsService, DeviceDwell } from '../services/copDwellsService';
import COPMapGL, { type CopCameraLegend } from '../components/COPMapGL';
import GeofenceTab from '../components/GeofenceTab';
import LiveStreamsTab from '../components/LiveStreamsTab';
import DeviceListPanel from '../components/DeviceListPanel';
import TimelineWidget from '../components/TimelineWidget';
import DwellTab from '../components/DwellTab';
import StatusTab from '../components/StatusTab';
import AlertsTab from '../components/AlertsTab';
import SOSAlertModal from '../components/SOSAlertModal';
import LiveStatusDashboard, { StatusFilterType } from '../components/LiveStatusDashboard';
import SmartAnalytics from '../components/SmartAnalytics';
import TeamChatTab from '../components/TeamChatTab';
import PttCaptionFeed from '../components/PttCaptionFeed';
import { copAlertsService, SOSMessage } from '../services/copAlertsService';
import { copTeamChatService } from '../services/copTeamChatService';
import { sseToDeviceLocationStatus } from '@signals/app-shell/hooks/useSSELocations';
import { normalizeDeviceLocation, normalizeDeviceLocations } from '../utils/normalizeDeviceType';
import { formatSpeedMph } from '../utils/speed';
import { COTRAVELER_PULSE_COLOR, devicesFollowedByCotraveler, formatCotravelerTickerText } from '../utils/cotravelerFollow';
import { useFeatureFlagsStore } from '@signals/app-shell/stores/featureFlagsStore';
import { useEffectiveAccess } from '@signals/app-shell/stores/accessControlStore';
import { hasPermission, filterByDataScopes } from '@signals/app-shell/types/accessControl';
import { useAccessDataScopeClamp } from '@signals/app-shell/hooks/useAccessDataScopeClamp';
import { useFilterStore, useDeviceStore, useMapStore, useAuthStore, useSettingsStore, AIMapMarker, deviceMatchesCOPFilters, deviceMatchesTimeWindow, computeCOPFilterDropdownOptions, sortedArraysEqual, type COPDeviceFilterSnapshot } from '@signals/app-shell/stores';
import { debugLog } from '@signals/app-shell/utils/debugLog';
import { withSseAccessToken } from '@signals/app-shell/utils/sseAuthUrl';
import {
  MissionEvent,
  MissionRoute,
  activeMissionThreats,
  missionEventsService,
} from '../services/missionEventsService';
import { getPreferredTimeZone } from '@signals/app-shell/utils/time';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';
import { useCourseLabels } from '@signals/app-shell/hooks/useCourseLabels';
import { useReferenceLayers } from '@signals/app-shell/hooks/useReferenceLayers';
import ReferenceLayersLegend from '@signals/app-shell/components/map/ReferenceLayersLegend';
import areaIntelService, { type TrafficIncident, type WeatherAlert } from '@signals/app-shell/services/areaIntelService';
import { listItinerarySubmissions, type ItinerarySubmission } from '@signals/app-shell/services/itineraryApi';

let DFPAlertsTab: React.ComponentType<any> | null = null;
let SignalWaterfallTab: React.ComponentType<any> | null = null;
let dfpAlertsService: any = null;
try {
  const rfModule = require('@signals/pathfinder-rf-ui');
  DFPAlertsTab = rfModule.DFPAlertsTab;
  SignalWaterfallTab = rfModule.SignalWaterfallTab;
  dfpAlertsService = rfModule.dfpAlertsService;
} catch { /* RF module not available */ }
// DFP alert shape mirrors modules/PathfinderRF/FE/src/services/dfpAlertsService.ts DFPAlert
type CopDFPAlert = {
  id: number;
  device_id: string;
  signal_type: string;
  anomaly_type: string;
  severity: 'critical' | 'warning' | 'info' | string;
  confidence_score?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  detected_at: string;
  observation_count?: number;
  evidence?: Record<string, any>;
  status?: string | null;
  feedback_verdict?: string | null;
};

type CopMapBounds = { north: number; south: number; east: number; west: number };

// Parse device timestamps ("YYYY-MM-DD HH:mm:ss" or ISO) to epoch ms; null if unparseable.
const parseLocationTimestamp = (ts?: string | null): number | null => {
  if (!ts) return null;
  const ms = new Date(ts.replace(' ', 'T')).getTime();
  return Number.isNaN(ms) ? null : ms;
};

// True when `incoming` is strictly older than `existing` (both parseable).
// Redis pub/sub delivers messages in arrival order, not GPS order, so a
// delayed message can carry an older fix; applying it would jump the marker
// backward. Fail open when either timestamp is missing/unparseable.
const isStalePositionUpdate = (
  incoming?: string | null,
  existing?: string | null
): boolean => {
  const incomingMs = parseLocationTimestamp(incoming);
  const existingMs = parseLocationTimestamp(existing);
  return incomingMs !== null && existingMs !== null && incomingMs < existingMs;
};

const getAlertZoomBucket = (zoom: number | null | undefined): number => {
  if (zoom == null || zoom < 8) return 0;
  if (zoom < 12) return 1;
  return 2;
};

const boundsContainBounds = (outer: CopMapBounds, inner: CopMapBounds): boolean => (
  outer.north >= inner.north &&
  outer.south <= inner.south &&
  outer.east >= inner.east &&
  outer.west <= inner.west
);

const isAreaAlertsStaleFor = (
  loadedBounds: CopMapBounds | null,
  loadedZoom: number | null,
  viewport: { bounds: CopMapBounds; zoom: number } | null
): boolean => {
  if (!loadedBounds || !viewport) return false;
  return (
    !boundsContainBounds(loadedBounds, viewport.bounds) ||
    getAlertZoomBucket(loadedZoom) !== getAlertZoomBucket(viewport.zoom)
  );
};

/** Default COP itinerary map window: 2 h past / 6 h future (user-overridable). */
const DEFAULT_ITINERARY_LOOKBACK_MINUTES = 120; // 2 hours
const DEFAULT_ITINERARY_LOOKAHEAD_HOURS = 6;

const COPDashboardClean: React.FC = () => {
  const { singular, plural } = useParticipantLabels();
  const { coursePlural, allLabel } = useCourseLabels();
  const preferredTimezoneSetting = useSettingsStore((state) => state.settings.temporal.preferredTimezone);
  const preferredTimezone = getPreferredTimeZone(preferredTimezoneSetting);
  const formatPreferredClockTime = useCallback((date: Date | null): string => {
    if (!date) return '';
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: preferredTimezone,
      timeZoneName: 'short',
    });
  }, [preferredTimezone]);
  const rfEnabled = useFeatureFlagsStore((s) => s.rfDashboard) && DFPAlertsTab != null;
  const intelEnabled = useFeatureFlagsStore((s) => s.intel);
  const classificationBannerMode = useFeatureFlagsStore((s) => s.classificationBannerMode);
  const isCommercialBanner = classificationBannerMode === 'commercial';
  useAccessDataScopeClamp();
  // Shared store with COPMapGL: whichever reference layers the user has turned
  // on in Map layers also show in the Legend tab. Fetching is left to the map.
  const enabledReferenceLayers = useReferenceLayers(false).enabledLayers;
  // Camera overlay summary pushed up from COPMapGL for the Legend tab.
  const [cameraLegend, setCameraLegend] = useState<CopCameraLegend | null>(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

  // Get primary color from theme's color scheme
  const primaryColor = theme.palette.primary.main;
  
  // Convert hex to rgb for rgba usage
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '76, 175, 80';
  };
  const primaryRgb = hexToRgb(primaryColor);

  // Theme-aware colors for styled components - uses theme's color scheme
  const themeColors = useMemo(() => ({
    cardBg: isDark 
      ? 'linear-gradient(135deg, rgba(30, 32, 40, 0.98), rgba(24, 26, 32, 0.99))'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(245, 247, 250, 0.95))',
    border: isDark ? `rgba(${primaryRgb}, 0.25)` : `rgba(${primaryRgb}, 0.3)`,
    headerBg: isDark ? `rgba(${primaryRgb}, 0.05)` : `rgba(${primaryRgb}, 0.05)`,
    accent: primaryColor,
    accentGlow: isDark ? `rgba(${primaryRgb}, 0.5)` : `rgba(${primaryRgb}, 0.3)`,
    text: isDark ? 'white' : '#1a1a2e',
    textSecondary: isDark ? '#9e9e9e' : '#666',
    textTertiary: isDark ? '#757575' : '#999',
    subtleBg: isDark ? `rgba(${primaryRgb}, 0.05)` : `rgba(${primaryRgb}, 0.05)`,
    progressBg: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    primaryRgb,
  }), [isDark, primaryColor, primaryRgb]);

  /** COP compact header + filter row: avoid white text on light `paper` in light mode */
  const topBarChrome = useMemo(() => {
    const fg = isDark ? '#ffffff' : theme.palette.text.primary;
    const icon = isDark ? '#ffffff' : theme.palette.action.active;
    return {
      fg,
      icon,
      headerBg: isDark ? theme.palette.background.paper : theme.palette.grey[100],
      selectSx: {
        color: fg,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
        '& .MuiSvgIcon-root': { color: icon },
      },
    };
  }, [isDark, theme]);

  // Mobile UI state
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  // Mobile panel has 3 states: 'minimized' (bar only), 'half' (50%), 'expanded' (80%)
  const [mobilePanelSize, setMobilePanelSize] = useState<'minimized' | 'half' | 'expanded'>('half');

  // Student itinerary map overlay. Pinned to the original single-entity selection
  // so companions (Exercise Support course / RP-SRP IDs) can be added to the map
  // without clearing the itinerary. Window: 2 h past / 6 h future (localStorage).
  const [showItineraryOnMap, setShowItineraryOnMap] = useState(false);
  const [itinerarySubmissions, setItinerarySubmissions] = useState<ItinerarySubmission[]>([]);
  const [itineraryEntityId, setItineraryEntityId] = useState<string | null>(null);
  const [itineraryLookbackMinutes, setItineraryLookbackMinutes] = useState(DEFAULT_ITINERARY_LOOKBACK_MINUTES);
  const [itineraryLookaheadHours, setItineraryLookaheadHours] = useState(DEFAULT_ITINERARY_LOOKAHEAD_HOURS);
  
  // Cycle through panel sizes on mobile
  const cycleMobilePanelSize = () => {
    setMobilePanelSize(prev => {
      if (prev === 'minimized') return 'half';
      if (prev === 'half') return 'expanded';
      return 'minimized';
    });
  };

  // Zustand stores
  const {
    selectedDate, setSelectedDate,
    selectedCourses, setSelectedCourses,
    selectedDeviceTypes, setSelectedDeviceTypes,
    selectedEntityTypes, setSelectedEntityTypes,
    selectedEntities, setSelectedEntities,
    courseFilterMode, deviceTypeFilterMode, entityTypeFilterMode, entityFilterMode,
  } = useFilterStore();
  
  const {
    devices, setDevices,
    devicesInBounds, setDevicesInBounds,
    selectedDevice, setSelectedDevice,
    followedDevice, setFollowedDevice,
    totalDevices, setTotalDevices,
    activeDevices, setActiveDevices,
    availableEntities, setAvailableEntities,
    isLoading: isRefreshing,
    setLoading: setIsRefreshing,
    lastUpdated, setLastUpdated,
  } = useDeviceStore();

  const {
    selectedBookmark,
    viewState: mapViewState, setViewState: setMapViewState,
    userHasInteractedWithMap, setUserHasInteractedWithMap,
    aiMarkers, setAiMarkers, clearAiMarkers,
  } = useMapStore();

  const authUser = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Per-user itinerary window prefs (COP map overlay)
  const itineraryPrefsKey = useMemo(
    () => `cop_itinerary_window_${authUser?.username || 'anonymous'}`,
    [authUser?.username]
  );

  useEffect(() => {
    try {
      const raw = localStorage.getItem(itineraryPrefsKey);
      if (!raw) {
        setItineraryLookbackMinutes(DEFAULT_ITINERARY_LOOKBACK_MINUTES);
        setItineraryLookaheadHours(DEFAULT_ITINERARY_LOOKAHEAD_HOURS);
        return;
      }
      const parsed = JSON.parse(raw) as { lookbackMinutes?: unknown; lookaheadHours?: unknown };
      const lookback = Number(parsed.lookbackMinutes);
      const lookahead = Number(parsed.lookaheadHours);
      setItineraryLookbackMinutes(
        Number.isFinite(lookback)
          ? Math.max(0, Math.min(24 * 60, Math.round(lookback)))
          : DEFAULT_ITINERARY_LOOKBACK_MINUTES
      );
      setItineraryLookaheadHours(
        Number.isFinite(lookahead)
          ? Math.max(0, Math.min(7 * 24, Math.round(lookahead * 10) / 10))
          : DEFAULT_ITINERARY_LOOKAHEAD_HOURS
      );
    } catch {
      setItineraryLookbackMinutes(DEFAULT_ITINERARY_LOOKBACK_MINUTES);
      setItineraryLookaheadHours(DEFAULT_ITINERARY_LOOKAHEAD_HOURS);
    }
  }, [itineraryPrefsKey]);

  const handleItineraryWindowChange = useCallback(
    (lookbackMinutes: number, lookaheadHours: number) => {
      setItineraryLookbackMinutes(lookbackMinutes);
      setItineraryLookaheadHours(lookaheadHours);
      try {
        localStorage.setItem(
          itineraryPrefsKey,
          JSON.stringify({ lookbackMinutes, lookaheadHours })
        );
      } catch (err) {
        console.warn('[COP] Failed to persist itinerary window prefs:', err);
      }
    },
    [itineraryPrefsKey]
  );

  // Local state for courses and dynamic filter options
  const [availableCourses, setAvailableCourses] = useState<string[]>([]);
  const [availableDeviceTypes, setAvailableDeviceTypes] = useState<string[]>([]);
  const [availableEntityTypes, setAvailableEntityTypes] = useState<string[]>([]);
  const lastDropdownOptionsRef = useRef({
    deviceTypes: [] as string[],
    entityTypes: [] as string[],
    entities: [] as string[],
  });

  const applyFilterDropdownOptions = useCallback((
    sourceDevices: DeviceLocationStatus[],
    filterSnapshot: COPDeviceFilterSnapshot,
    pruneSelections = false
  ) => {
    const options = computeCOPFilterDropdownOptions(sourceDevices, filterSnapshot);
    const prev = lastDropdownOptionsRef.current;

    if (!sortedArraysEqual(options.deviceTypes, prev.deviceTypes)) {
      prev.deviceTypes = options.deviceTypes;
      setAvailableDeviceTypes(options.deviceTypes);
    }
    if (!sortedArraysEqual(options.entityTypes, prev.entityTypes)) {
      prev.entityTypes = options.entityTypes;
      setAvailableEntityTypes(options.entityTypes);
    }
    if (!sortedArraysEqual(options.entities, prev.entities)) {
      prev.entities = options.entities;
      setAvailableEntities(options.entities);
    }

    if (!pruneSelections) {
      return;
    }

    const store = useFilterStore.getState();
    if (store.selectedDeviceTypes.length > 0) {
      const valid = store.selectedDeviceTypes.filter((t) => options.deviceTypes.includes(t));
      if (valid.length !== store.selectedDeviceTypes.length) {
        store.setSelectedDeviceTypes(valid);
      }
    }
    if (store.selectedEntityTypes.length > 0) {
      const valid = store.selectedEntityTypes.filter((t) => options.entityTypes.includes(t));
      if (valid.length !== store.selectedEntityTypes.length) {
        store.setSelectedEntityTypes(valid);
      }
    }
    if (store.selectedEntities.length > 0) {
      const valid = store.selectedEntities.filter((e) => options.entities.includes(e));
      if (valid.length !== store.selectedEntities.length) {
        store.setSelectedEntities(valid);
      }
    }
  }, [setAvailableEntities]);

  // Shared palette for dynamic commercial entity-type chips. Deterministic so the
  // same type gets the same color across the ticker, legend, timeline and status.
  const ENTITY_TYPE_PALETTE = React.useMemo(
    () => ['#4caf50', '#ff9800', '#f44336', '#29b6f6', '#ab47bc', '#26a69a', '#ec407a', '#7e57c2'],
    [],
  );
  const getEntityTypeColor = React.useCallback((type: string, index: number) => {
    if (!isCommercialBanner) {
      // Gov side: stable color map keyed to fixed taxonomy.
      if (type === 'Student') return '#4caf50';
      if (type === 'RP') return '#ff9800';
      if (type === 'SRP') return '#f44336';
    }
    return ENTITY_TYPE_PALETTE[index % ENTITY_TYPE_PALETTE.length];
  }, [ENTITY_TYPE_PALETTE, isCommercialBanner]);

  // A small helper for legend descriptions; falls back to a generic label when the
  // entity type is commercial-specific and we have no curated description for it.
  const describeEntityType = React.useCallback((type: string) => {
    if (type === 'Student') return 'Training personnel';
    if (type === 'RP') return 'Role Player';
    if (type === 'SRP') return 'Surveillance Team';
    return `${type} personnel`;
  }, []);

  // Derive the set of entity types actually present in the current device list.
  // In commercial mode this drives the status ticker, Legend tab, and Reporting
  // Status widget so we never render stale Student/RP/SRP rows against data that
  // has none of them.
  const entityTypeBreakdown = React.useMemo(() => {
    const counts = new Map<string, number>();
    devices.forEach((d: any) => {
      const et = (d.entity_type || '').trim();
      if (!et) return;
      counts.set(et, (counts.get(et) || 0) + 1);
    });
    const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
    return sorted.map(([type, count], idx) => ({
      type,
      count,
      color: getEntityTypeColor(type, idx),
      description: describeEntityType(type),
    }));
  }, [devices, getEntityTypeColor, describeEntityType]);
  
  // Derive a single course string for child components that expect it
  const selectedCourseForChild = selectedCourses.length === 1 ? selectedCourses[0] : 'All Courses';
  // Multi-select "All Types" is represented as either [] OR every available
  // option selected. With only "Phone" in the dropdown, selecting it still
  // renders "All Types" but previously passed device_type=Phone to timeline
  // APIs — which exact-match against device_tracks.device_type='cellphone'
  // and returned zero buckets while the Devices list (client-normalized)
  // stayed full.
  // Treat unknown option catalogs as All so a persisted single selection
  // (e.g. ["Phone"]) cannot fire timeline/dwells filters before dropdown
  // options hydrate — that race used to send device_type=Phone and zero
  // /timeline-dwells (commercial dwells have null device_track_id).
  const isAllDeviceTypesSelected =
    selectedDeviceTypes.length === 0 ||
    availableDeviceTypes.length === 0 ||
    selectedDeviceTypes.length === availableDeviceTypes.length;
  const isAllEntityTypesSelected =
    selectedEntityTypes.length === 0 ||
    availableEntityTypes.length === 0 ||
    selectedEntityTypes.length === availableEntityTypes.length;
  const timelineDeviceType =
    !isAllDeviceTypesSelected && selectedDeviceTypes.length === 1
      ? selectedDeviceTypes[0]
      : null;
  const timelineEntityType =
    !isAllEntityTypesSelected && selectedEntityTypes.length === 1
      ? selectedEntityTypes[0]
      : null;
  const loadingRef = useRef(false);
  
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const effectiveAccess = useEffectiveAccess();
  const [chatUnread, setChatUnread] = useState(0);
  const [chatSubTab, setChatSubTab] = useState<'chat' | 'ptt'>('chat');
  // DFP tab tier: default to 'all' so every active (unacknowledged) detection
  // is visible on open. 'actionable' narrows to the high-precision threat
  // tier (watchlist / cotraveler / proximity), 'insights' to the broader
  // algorithmic anomaly feed.
  const [dfpCategory, setDfpCategory] = useState<'actionable' | 'insights' | 'all'>('all');
  
  const [geofences, setGeofences] = useState<any[]>([]);
  const [isDrawingGeofence, setIsDrawingGeofence] = useState(false); //huh?
  const [selectedGeofenceId, setSelectedGeofenceId] = useState<number | null>(null);
  const [pendingGeofenceGeoJSON, setPendingGeofenceGeoJSON] = useState<any>(null);
  const [selectedStatusEntries, setSelectedStatusEntries] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilterType>(null);

  // SOS Alerts state
  const [sosAlertMessage, setSOSAlertMessage] = useState<any | null>(null);
  const [sosMessages, setSOSMessages] = useState<any[]>([]);
  const [acknowledgedSOSIds, setAcknowledgedSOSIds] = useState<Set<number>>(() => {
    try {
      const stored = localStorage.getItem('sos_acknowledged_ids');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) return new Set(parsed);
      }
    } catch {}
    return new Set();
  });
  const [sosAckError, setSOSAckError] = useState<string | null>(null);
  const sosAckInFlightRef = useRef<Set<number>>(new Set());
  const acknowledgedSOSIdsRef = useRef<Set<number>>(acknowledgedSOSIds);

  // Keep ref in sync with state so polling callbacks always see latest values
  useEffect(() => {
    acknowledgedSOSIdsRef.current = acknowledgedSOSIds;
    if (acknowledgedSOSIds.size > 0) {
      const arr = Array.from(acknowledgedSOSIds).slice(-200);
      localStorage.setItem('sos_acknowledged_ids', JSON.stringify(arr));
    }
  }, [acknowledgedSOSIds]);

  // SSE Real-time updates (enabled by default for real-time device updates)
  const [useSSE, setUseSSE] = useState<boolean>(true);
  const useSSERef = useRef(useSSE);
  useEffect(() => {
    useSSERef.current = useSSE;
  }, [useSSE]);
  
  // Store all raw SSE devices (unfiltered) for re-filtering when filters change
  const sseRawDevicesRef = useRef<Map<string, DeviceLocationStatus>>(
    new Map<string, DeviceLocationStatus>()
  );
  
  // Ticker visibility toggle
  const [showTicker, setShowTicker] = useState<boolean>(true);
  
  // Selected dwell for map display
  const [selectedDwell, setSelectedDwell] = useState<{
    latitude: number;
    longitude: number;
    entity_id: string;
    duration: string;
    start_time: string;
  } | null>(null);
  
  // Store previous map view state for restoring after dwell deselection
  const [previousMapView, setPreviousMapView] = useState<{
    longitude: number;
    latitude: number;
    zoom: number;
  } | null>(null);
  
  // Dwells data for Smart Analytics
  const [dwellsData, setDwellsData] = useState<DeviceDwell[]>([]);
  
  // Map bounds for timeline filtering
  const [mapBounds, setMapBounds] = useState<{ north: number; south: number; east: number; west: number } | null>(null);
  const [areaAlertViewport, setAreaAlertViewport] = useState<{
    bounds: { north: number; south: number; east: number; west: number };
    zoom: number;
  } | null>(null);
  const [trafficIncidents, setTrafficIncidents] = useState<TrafficIncident[]>([]);
  const [weatherAlerts, setWeatherAlerts] = useState<WeatherAlert[]>([]);
  const [areaAlertsLoading, setAreaAlertsLoading] = useState(false);
  const [areaAlertsError, setAreaAlertsError] = useState<string | null>(null);
  const [areaAlertsLoadedBounds, setAreaAlertsLoadedBounds] = useState<{ north: number; south: number; east: number; west: number } | null>(null);
  const [areaAlertsLoadedAt, setAreaAlertsLoadedAt] = useState<string | null>(null);
  const [areaAlertsLoadedZoom, setAreaAlertsLoadedZoom] = useState<number | null>(null);
  const areaAlertCount = trafficIncidents.length + weatherAlerts.length;

  type CopTabId =
    | 'stats'
    | 'events'
    | 'devices'
    | 'status'
    | 'legend'
    | 'dwells'
    | 'geofences'
    | 'streams'
    | 'alerts'
    | 'chat'
    | 'dfp'
    | 'rf_feed';

  const [watchLiveDeviceId, setWatchLiveDeviceId] = useState<string | null>(null);

  const liveStreamCount = useMemo(
    () => devices.filter((d) => d.is_streaming).length,
    [devices]
  );

  const handleLocateStreamingDevice = useCallback((device: DeviceLocationStatus) => {
    setSelectedDevice(device);
    if (Number.isFinite(device.latitude) && Number.isFinite(device.longitude)) {
      setMapViewState({
        longitude: device.longitude,
        latitude: device.latitude,
        zoom: Math.max(mapViewState?.zoom ?? 14, 15),
      });
    }
  }, [setSelectedDevice, setMapViewState, mapViewState?.zoom]);

  const handleWatchStreamingOnMap = useCallback((device: DeviceLocationStatus) => {
    handleLocateStreamingDevice(device);
    setWatchLiveDeviceId(device.device_id);
  }, [handleLocateStreamingDevice]);

  const visibleCopTabs = useMemo(() => {
    const defs: Array<{ id: CopTabId; permission?: string; rfOnly?: boolean }> = [
      { id: 'stats', permission: 'tab.cop.stats' },
      { id: 'events', permission: 'tab.cop.events' },
      { id: 'devices', permission: 'tab.cop.devices' },
      { id: 'status', permission: 'tab.cop.status' },
      { id: 'legend', permission: 'tab.cop.legend' },
      { id: 'dwells', permission: 'tab.cop.dwells' },
      { id: 'geofences', permission: 'tab.cop.geofences' },
      { id: 'streams' },
      { id: 'alerts' },
      { id: 'chat', permission: 'tab.cop.team_chat' },
      { id: 'dfp', permission: 'tab.cop.dfp', rfOnly: true },
      { id: 'rf_feed', permission: 'tab.cop.rf_feed', rfOnly: true },
    ];
    return defs.filter((t) => {
      if (t.rfOnly && !rfEnabled) return false;
      if (t.permission && !hasPermission(effectiveAccess, t.permission)) return false;
      return true;
    });
  }, [effectiveAccess, rfEnabled]);

  const activeTabId: CopTabId | undefined = visibleCopTabs[Math.min(activeTab, Math.max(visibleCopTabs.length - 1, 0))]?.id;

  useEffect(() => {
    if (visibleCopTabs.length === 0) return;
    if (activeTab >= visibleCopTabs.length) setActiveTab(0);
  }, [visibleCopTabs, activeTab]);

  useEffect(() => {
    if (activeTabId === 'dwells' || !selectedDwell) return;
    setSelectedDwell(null);
    setPreviousMapView(null);
  }, [activeTabId, selectedDwell]);

  const openCopTab = useCallback((id: CopTabId) => {
    const idx = visibleCopTabs.findIndex((t) => t.id === id);
    if (idx >= 0) setActiveTab(idx);
  }, [visibleCopTabs]);

  const areaAlertsStale = useMemo(
    () => isAreaAlertsStaleFor(areaAlertsLoadedBounds, areaAlertsLoadedZoom, areaAlertViewport),
    [areaAlertViewport, areaAlertsLoadedBounds, areaAlertsLoadedZoom]
  );

  const loadAreaAlerts = useCallback(async ({
    bounds,
    zoom,
    force = false,
  }: {
    bounds: CopMapBounds;
    zoom: number;
    force?: boolean;
  }) => {
    const loadedAtMs = areaAlertsLoadedAt ? new Date(areaAlertsLoadedAt).getTime() : 0;
    const cacheFresh = loadedAtMs > 0 && Date.now() - loadedAtMs < 5 * 60 * 1000;
    const requestViewport = { bounds, zoom };

    if (
      !force &&
      cacheFresh &&
      areaAlertsLoadedBounds &&
      !isAreaAlertsStaleFor(areaAlertsLoadedBounds, areaAlertsLoadedZoom, requestViewport)
    ) {
      return;
    }

    setAreaAlertsLoading(true);
    setAreaAlertsError(null);

    try {
      const response = await areaIntelService.getAreaAlerts(bounds, {
        includeWeather: true,
        zoom,
      });
      setTrafficIncidents(response.incidents || []);
      setWeatherAlerts(response.weather_alerts || []);
      setAreaAlertsLoadedBounds(bounds);
      setAreaAlertsLoadedZoom(zoom);
      setAreaAlertsLoadedAt(response.last_updated || new Date().toISOString());
      setAreaAlertViewport(requestViewport);
    } catch (err: any) {
      console.error('[COP] Error loading area alerts:', err);
      setAreaAlertsError(err?.message || 'Failed to load traffic and weather alerts.');
    } finally {
      setAreaAlertsLoading(false);
    }
  }, [areaAlertsLoadedAt, areaAlertsLoadedBounds, areaAlertsLoadedZoom]);

  const handleAreaAlertClick = useCallback((alert: TrafficIncident | WeatherAlert) => {
    const latitude = 'latitude' in alert ? alert.latitude : undefined;
    const longitude = 'longitude' in alert ? alert.longitude : undefined;
    if (latitude == null || longitude == null) return;

    setMapViewState({
      latitude,
      longitude,
      zoom: Math.max(areaAlertViewport?.zoom || 0, 14),
    });
    setUserHasInteractedWithMap(true);
  }, [areaAlertViewport?.zoom, setMapViewState, setUserHasInteractedWithMap]);

  // DFP signal-anomaly alerts (from PathfinderRF module).
  // Populated by dfpAlertsService polling when rfEnabled is true.
  const [dfpAlerts, setDFPAlerts] = useState<CopDFPAlert[]>([]);
  const [selectedDfpAlert, setSelectedDfpAlert] = useState<CopDFPAlert | null>(null);
  // Emitter localization overlay (RSSI/RTT/TDOA estimate + movement track)
  // for the currently expanded DFP alert, plotted alongside the observer pin.
  const [dfpEmitter, setDfpEmitter] = useState<any | null>(null);
  // Stashed view so we can restore the map after deselecting a DFP alert.
  const [preDfpAlertView, setPreDfpAlertView] = useState<{
    longitude: number;
    latitude: number;
    zoom: number;
  } | null>(null);
  
  // Missions under way: the planned route the COP draws, and every event raised
  // against it. Snapshot on mount, live from then on via its own SSE stream.
  const [missionRoutes, setMissionRoutes] = useState<MissionRoute[]>([]);
  const [missionEvents, setMissionEvents] = useState<MissionEvent[]>([]);
  const [missionPromoteTarget, setMissionPromoteTarget] = useState<MissionEvent | null>(null);
  const [missionPromoteReason, setMissionPromoteReason] = useState('');
  const [missionPromoteSaving, setMissionPromoteSaving] = useState(false);
  const [missionPromoteError, setMissionPromoteError] = useState<string | null>(null);
  const [missionPromoteNote, setMissionPromoteNote] = useState<string | null>(null);

  // Area analysis state - for drawing areas on map and asking AI about them
  const [isDrawingAnalysisArea, setIsDrawingAnalysisArea] = useState(false);
  const [analysisAreaGeometry, setAnalysisAreaGeometry] = useState<{
    type: 'Polygon';
    coordinates: number[][][];
  } | null>(null);
  
  // Map view before the first PTT transcript zoom; restored when the line is deselected.
  const [prePttZoomView, setPrePttZoomView] = useState<{
    longitude: number;
    latitude: number;
    zoom: number;
  } | null>(null);

  // Entity routes state - for showing historical tracks on the map
  const [entityRoutes, setEntityRoutes] = useState<Array<{
    entityId: string;
    tracks: Array<{ latitude: number; longitude: number; timestamp: string; accuracy?: number }>;
    color?: string;
  }>>([]);

  // Dwell circles state - for showing dwell locations as circles on the map
  const [dwellCircles, setDwellCircles] = useState<Array<{
    id: string;
    latitude: number;
    longitude: number;
    radiusMeters: number;
    entityId: string;
    deviceId?: string;
    durationSeconds: number;
    visitCount?: number;
    startTime?: string;
    endTime?: string;
  }>>([]);
  
  // SSE connection status (tracked on the direct EventSource below — not useSSELocations,
  // which would open a duplicate stream to the same endpoint).
  const [sseConnected, setSseConnected] = useState(false);
  const [sseEventsReceived, setSseEventsReceived] = useState(0);
  const sseStatus = useMemo(
    () => ({
      connected: sseConnected,
      eventsReceived: sseEventsReceived,
      lastEventAt: null as Date | null,
      reconnectAttempts: 0,
      error: null as string | null,
    }),
    [sseConnected, sseEventsReceived]
  );

  const syncSseFilteredDevicesRef = useRef<((pruneSelections?: boolean) => void) | null>(null);
  const loadDevicesRef = useRef<(() => Promise<void>) | null>(null);

  const loadGeofences = async (clearCache = false) => {
    try {
      // Clear cache if requested (e.g., after create/delete)
      if (clearCache) {
        copDataService.clearCacheEntry('geofences', { activeOnly: true });
      }
      const data = await copDataService.getGeofences(true);
      setGeofences(data);
    } catch (err: any) {
      // Only log non-abort errors
      if (err?.name !== 'AbortError') {
        console.error('[COPDashboard] ❌ Failed to load geofences:', err);
      }
    }
  };

  // Auto-zoom to the densest cluster of *active* devices (not a hardcoded city).
  const calculateAutoZoom = useCallback(() => {
    const isValid = (d: DeviceLocationStatus) =>
      typeof d.latitude === 'number' &&
      typeof d.longitude === 'number' &&
      Number.isFinite(d.latitude) &&
      Number.isFinite(d.longitude) &&
      !(d.latitude === 0 && d.longitude === 0);

    const withCoords = devices.filter(isValid);
    if (withCoords.length === 0) return;

    // Prefer recently-active devices so stale leftovers don't win the zoom.
    const active = withCoords.filter((d) => (d.last_seen_minutes_ago ?? 999) <= 30);
    const pool = active.length > 0 ? active : withCoords;

    // ~11km grid cells — tighter than the old 55km cells so a sparse
    // Tucson leftover can't outrank a tight active cluster elsewhere.
    const GRID_SIZE = 0.1;
    const densityMap = new Map<string, DeviceLocationStatus[]>();

    pool.forEach((device) => {
      const key = `${Math.round(device.latitude / GRID_SIZE)},${Math.round(device.longitude / GRID_SIZE)}`;
      const bucket = densityMap.get(key);
      if (bucket) bucket.push(device);
      else densityMap.set(key, [device]);
    });

    let densest: DeviceLocationStatus[] = pool;
    let maxDensity = 0;
    densityMap.forEach((cell) => {
      if (cell.length > maxDensity) {
        maxDensity = cell.length;
        densest = cell;
      }
    });

    // Include neighboring cells so a cluster straddling a grid edge stays together.
    if (densest.length > 0 && densest.length < pool.length) {
      const seed = densest[0];
      const latIdx = Math.round(seed.latitude / GRID_SIZE);
      const lonIdx = Math.round(seed.longitude / GRID_SIZE);
      const neighborhood: DeviceLocationStatus[] = [];
      for (let dlat = -1; dlat <= 1; dlat++) {
        for (let dlon = -1; dlon <= 1; dlon++) {
          const arr = densityMap.get(`${latIdx + dlat},${lonIdx + dlon}`);
          if (arr) neighborhood.push(...arr);
        }
      }
      if (neighborhood.length > 0) densest = neighborhood;
    }

    let sumLat = 0;
    let sumLon = 0;
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLon = Infinity;
    let maxLon = -Infinity;
    densest.forEach((device) => {
      sumLat += device.latitude;
      sumLon += device.longitude;
      minLat = Math.min(minLat, device.latitude);
      maxLat = Math.max(maxLat, device.latitude);
      minLon = Math.min(minLon, device.longitude);
      maxLon = Math.max(maxLon, device.longitude);
    });

    const centerLat = sumLat / densest.length;
    const centerLon = sumLon / densest.length;
    const maxSpan = Math.max(maxLat - minLat, maxLon - minLon);

    let zoom = 10;
    if (maxSpan < 0.01) zoom = 13;
    else if (maxSpan < 0.05) zoom = 11;
    else if (maxSpan < 0.1) zoom = 10;
    else if (maxSpan < 0.5) zoom = 9;
    else if (maxSpan < 1) zoom = 8;
    else zoom = 7;

    if (!Number.isFinite(centerLat) || !Number.isFinite(centerLon)) return;

    setMapViewState({
      longitude: centerLon,
      latitude: centerLat,
      zoom,
    });
  }, [devices, setMapViewState]);

  // Auto-zoom once when devices first become available (Auto bookmark + no
  // manual pan). Retries while coords are still missing; locks after a
  // successful write so polling doesn't keep yanking the camera.
  const hasParentAutoZoomedRef = useRef(false);
  useEffect(() => {
    if (selectedBookmark !== 'Auto') {
      hasParentAutoZoomedRef.current = false;
      return;
    }
    if (userHasInteractedWithMap) return;
    if (hasParentAutoZoomedRef.current) return;
    if (devices.length === 0) return;

    const hasMappable = devices.some(
      (d) =>
        Number.isFinite(d.latitude) &&
        Number.isFinite(d.longitude) &&
        !(d.latitude === 0 && d.longitude === 0),
    );
    if (!hasMappable) return;

    const timer = setTimeout(() => {
      calculateAutoZoom();
      const after = useMapStore.getState().viewState;
      if (after && Number.isFinite(after.latitude) && Number.isFinite(after.longitude)) {
        hasParentAutoZoomedRef.current = true;
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [devices, selectedBookmark, userHasInteractedWithMap, calculateAutoZoom]);

  const loadDevices = useCallback(async () => {
    // Prevent concurrent loads
    if (loadingRef.current) {
      return;
    }
    
    try {
      loadingRef.current = true;

      // Pass the time filter to only get devices within the selected time range
      const response = await copDataService.getDeviceLocations(false, selectedDate);

      // Collapse cellphone/smartphone/samsungphone variants to a single
      // canonical "Phone" before any filtering, dropdown extraction, or
      // breakdown aggregation runs. Without this the LiveStatusDashboard
      // splits a single physical bucket into separate "Phone" and "Cellphone"
      // rows, which both reads as "more unassigned" and prevents the device
      // type filter from selecting all phones at once.
      response.devices = normalizeDeviceLocations(response.devices);
      // Enforce app-managed data scopes (SSE path also applies this; keep REST consistent)
      response.devices = filterByDataScopes(response.devices, effectiveAccess);

      const filterSnapshot: COPDeviceFilterSnapshot = {
        selectedCourses,
        courseFilterMode,
        selectedDeviceTypes,
        deviceTypeFilterMode,
        selectedEntityTypes,
        entityTypeFilterMode,
        selectedEntities,
        entityFilterMode,
      };
      // Shared matcher (incl. explicit RP/SRP companions across courses)
      const filteredDevices = response.devices.filter((d) =>
        deviceMatchesCOPFilters(d, filterSnapshot)
      );

      if (useSSERef.current) {
        // Seed devices REST knows about that SSE hasn't delivered yet, and
        // refresh any device whose REST position is strictly newer than the
        // SSE copy (self-heals a silently dead stream). The timestamp check
        // guarantees we never regress a marker that SSE already advanced.
        for (const d of response.devices) {
          const existing = sseRawDevicesRef.current.get(d.device_id);
          if (!existing) {
            sseRawDevicesRef.current.set(d.device_id, d);
          } else if (isStalePositionUpdate(existing.timestamp, d.timestamp)) {
            sseRawDevicesRef.current.set(d.device_id, {
              ...d,
              course_id: d.course_id ?? existing.course_id,
              entity_id: d.entity_id ?? existing.entity_id,
              entity_type: d.entity_type ?? existing.entity_type,
              device_type: d.device_type ?? existing.device_type,
            });
          }
        }
        syncSseFilteredDevicesRef.current?.(true);
      } else {
        setDevices(filteredDevices);

        const activeDeviceCount = response.devices.filter(
          (d) => (d.last_seen_minutes_ago ?? 999) <= 30
        ).length;
        applyFilterDropdownOptions(response.devices, filterSnapshot, true);
        setTotalDevices(response.devices.length);
        setActiveDevices(activeDeviceCount);
        setLastUpdated(new Date());
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        console.error('[COP] Failed to load devices:', err);
      }
    } finally {
      loadingRef.current = false;
    }
  }, [
    selectedDate,
    selectedCourses,
    selectedDeviceTypes,
    selectedEntityTypes,
    selectedEntities,
    courseFilterMode,
    deviceTypeFilterMode,
    entityTypeFilterMode,
    entityFilterMode,
    applyFilterDropdownOptions,
    effectiveAccess,
  ]);

useEffect(() => {
    if (!isAuthenticated) return;

    const timer = setTimeout(() => {
      loadGeofences();
    }, 100);

    return () => clearTimeout(timer);
  }, [isAuthenticated]);

  // Periodic refresh of geofences to sync across users
  useEffect(() => {
    if (!isAuthenticated) return;

    // Set up interval to refresh geofences every 30 seconds
    const interval = setInterval(() => {
      loadGeofences();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Load courses on component mount - stable catalog for the course dropdown
  useEffect(() => {
    const loadCourses = async () => {
      try {
        const courses = await copApiService.getCourses();
        debugLog('cop', '[COP Dashboard] Loaded courses:', courses);
        setAvailableCourses(courses);

        const { selectedCourses: persistedCourses, setSelectedCourses } = useFilterStore.getState();
        if (persistedCourses.length > 0) {
          const valid = persistedCourses.filter((course) => courses.includes(course));
          if (valid.length !== persistedCourses.length) {
            setSelectedCourses(valid);
          }
        }
      } catch (error) {
        console.error('[COP Dashboard] Failed to load courses:', error);
      }
    };

    loadCourses();
  }, []);

  // Load dwells for Smart Analytics
  useEffect(() => {
    const loadDwells = async () => {
      try {
        // Use the same All-types resolution as Timeline/Status so a single
        // persisted "Phone" selection (rendered as All Types) does not filter
        // the analytics dwell fetch incorrectly.
        const deviceType = timelineDeviceType || undefined;
        const entityType = timelineEntityType || undefined;
        const response = await copDwellsService.getDwells(
          selectedCourseForChild,
          deviceType,
          entityType,
          7, // Last 7 days
          200 // Get up to 200 dwells for analytics
        );
        // Filter client-side if multiple types/courses selected
        let dwells = response.dwells;
        if (selectedCourses.length > 1) {
          dwells = dwells.filter(d => selectedCourses.includes((d as any).course_id || ''));
        }
        if (selectedDeviceTypes.length > 1) {
          dwells = dwells.filter(d => selectedDeviceTypes.includes(d.device_type || ''));
        }
        if (selectedEntityTypes.length > 1) {
          dwells = dwells.filter(d => selectedEntityTypes.includes((d as any).entity_type || ''));
        }
        setDwellsData(dwells);
      } catch (err) {
        console.error('[COP Dashboard] Error loading dwells for analytics:', err);
      }
    };
    
    // Load with small delay to not compete with other requests
    const timer = setTimeout(() => {
      loadDwells();
    }, 500);

    return () => clearTimeout(timer);
  }, [selectedCourseForChild, selectedCourses, selectedDeviceTypes, selectedEntityTypes, timelineDeviceType, timelineEntityType]);

  // Debounced load when filters change (polling mode only — SSE re-filters locally)
  useEffect(() => {
    if (useSSE) {
      return;
    }

    const timer = setTimeout(() => {
      loadDevices();
    }, 300); // 300ms debounce
    
    return () => {
      clearTimeout(timer);
      // Cancel any pending requests
      copDataService.cancelAllRequests();
    };
  }, [selectedCourses, selectedDeviceTypes, selectedEntityTypes, selectedEntities, selectedDate, loadDevices, useSSE]);
  
  // Auto-refresh device positions every 2.5 seconds for near real-time updates
  // Only run polling if SSE is disabled
  useEffect(() => {
    if (useSSE) {
      // SSE is handling updates, no need to poll
      return;
    }

    loadDevices();

    const intervalId = setInterval(() => {
      copDataService.clearCache(); // Clear cache to force fresh data
      loadDevices();
    }, 2500); // 2.5 seconds
    
    return () => {
      clearInterval(intervalId);
    };
  }, [loadDevices, useSSE]);

  // Track SSE device IDs and connection
// const [sseDeviceIds, setSseDeviceIds] = useState<Set<string>>(new Set());
const setSseDeviceIds = (_value: any) => {};
  const eventSourceRef = useRef<EventSource | null>(null);
  const sseActiveRef = useRef(false);
  // Batching for SSE updates - prevents UI freezing from rapid updates
  const ssePendingUpdatesRef = useRef<Map<string, DeviceLocationStatus>>(
    new Map<string, DeviceLocationStatus>()
  );
  const sseFlushTimerRef = useRef<NodeJS.Timeout | null>(null);
  const SSE_BATCH_INTERVAL = 250; // Flush updates every 250ms
  // Watchdog: recreate the EventSource when it dies permanently (e.g. the
  // access token embedded in the SSE URL expired, so the browser's automatic
  // reconnect gets a 401 and EventSource goes to CLOSED forever) or when the
  // stream goes silent (server heartbeats every 15s, so 45s = 3 missed).
  const sseWatchdogTimerRef = useRef<NodeJS.Timeout | null>(null);
  const sseLastEventAtRef = useRef<number>(Date.now());
  const SSE_WATCHDOG_INTERVAL = 10000;
  const SSE_SILENCE_THRESHOLD = 45000;

  const syncSseFilteredDevices = useCallback((pruneSelections = false) => {
    if (!sseActiveRef.current || sseRawDevicesRef.current.size === 0) {
      return;
    }

    const rawDevices = Array.from(sseRawDevicesRef.current.values()) as DeviceLocationStatus[];
    // Drop devices outside app-managed data scopes before UI filters
    const scopeFiltered = filterByDataScopes(rawDevices, effectiveAccess);
    const filters = useFilterStore.getState();
    const filterSnapshot: COPDeviceFilterSnapshot = {
      selectedCourses: filters.selectedCourses,
      courseFilterMode: filters.courseFilterMode,
      selectedDeviceTypes: filters.selectedDeviceTypes,
      deviceTypeFilterMode: filters.deviceTypeFilterMode,
      selectedEntityTypes: filters.selectedEntityTypes,
      entityTypeFilterMode: filters.entityTypeFilterMode,
      selectedEntities: filters.selectedEntities,
      entityFilterMode: filters.entityFilterMode,
    };

    const timeWindow = filters.selectedDate;

    const timeFilteredDevices = scopeFiltered.filter((d) =>
      deviceMatchesTimeWindow(d, timeWindow, preferredTimezone)
    );
    const filtered = timeFilteredDevices.filter((d) =>
      deviceMatchesCOPFilters(d, filterSnapshot)
    );
    setDevices(filtered);
    setLastUpdated(new Date());
    setTotalDevices(timeFilteredDevices.length);
    setActiveDevices(
      timeFilteredDevices.filter((d) => (d.last_seen_minutes_ago ?? 999) <= 30).length
    );
    applyFilterDropdownOptions(timeFilteredDevices, filterSnapshot, pruneSelections);
  }, [setDevices, setLastUpdated, setTotalDevices, setActiveDevices, applyFilterDropdownOptions, preferredTimezone, effectiveAccess]);

  useEffect(() => {
    syncSseFilteredDevicesRef.current = syncSseFilteredDevices;
  }, [syncSseFilteredDevices]);
  
  // Function to flush batched SSE updates to state
  const flushSSEUpdates = useCallback(() => {
    if (!sseActiveRef.current || ssePendingUpdatesRef.current.size === 0) return;

    const pendingCount = ssePendingUpdatesRef.current.size;
    ssePendingUpdatesRef.current = new Map<string, DeviceLocationStatus>();
    syncSseFilteredDevices();
    debugLog('cop', '[COPDashboard] 📦 Flushing SSE batch:', pendingCount, 'updates');
  }, [syncSseFilteredDevices]);
  
  // DIRECT SSE - when enabled, CLEAR devices and only show SSE data
  useEffect(() => {
    if (!useSSE) {
      setSseConnected(false);
      setSseEventsReceived(0);
      setSseDeviceIds(new Set());
      sseActiveRef.current = false;
      if (sseFlushTimerRef.current) {
        clearInterval(sseFlushTimerRef.current);
        sseFlushTimerRef.current = null;
      }
      if (sseWatchdogTimerRef.current) {
        clearInterval(sseWatchdogTimerRef.current);
        sseWatchdogTimerRef.current = null;
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      return;
    }
    
    // Prevent duplicate connections
    if (eventSourceRef.current && eventSourceRef.current.readyState !== EventSource.CLOSED) {
      debugLog('cop', '[COPDashboard] SSE already connected, skipping');
      return;
    }
    
    sseActiveRef.current = true;
    
    sseRawDevicesRef.current = new Map<string, DeviceLocationStatus>();
    ssePendingUpdatesRef.current = new Map<string, DeviceLocationStatus>();
    lastDropdownOptionsRef.current = {
      deviceTypes: [],
      entityTypes: [],
      entities: [],
    };
    
    // Start batch flush timer
    sseFlushTimerRef.current = setInterval(flushSSEUpdates, SSE_BATCH_INTERVAL);

    const connectSSE = () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      // Token is resolved at connect time; reconnects via this function always
      // get a fresh one (the browser's built-in retry reuses the stale URL).
      const sseUrl = withSseAccessToken(`${apiUrl}/sse/locations`);
      debugLog('cop', '[COPDashboard] 🚀 Starting SSE connection to:', sseUrl);

      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;
      sseLastEventAtRef.current = Date.now();

      eventSource.onopen = () => {
        setSseConnected(true);
        sseLastEventAtRef.current = Date.now();
        debugLog('cop', '[COPDashboard] ✅ SSE Connection OPEN!');
      };

      eventSource.addEventListener('connected', (e) => {
        sseLastEventAtRef.current = Date.now();
        debugLog('cop', '[COPDashboard] ✅ SSE Server confirmed:', e.data);
      });

      eventSource.addEventListener('location', (e: MessageEvent) => {
        if (!sseActiveRef.current) return;

        try {
          const loc = JSON.parse(e.data);
          sseLastEventAtRef.current = Date.now();
          // Normalize device_type at the SSE boundary so cellphone/smartphone/
          // samsungphone all collapse to "Phone" before they enter our state.
          // Doing it here means every downstream consumer (filters, dropdowns,
          // LiveStatusDashboard breakdown) sees canonical values and we don't
          // have to sprinkle .toLowerCase().includes('phone') checks elsewhere.
          const normalized = normalizeDeviceLocation(sseToDeviceLocationStatus(loc));
          const existing = sseRawDevicesRef.current.get(loc.device_id);

          // Redis pub/sub has no ordering guarantee: a delayed message can
          // carry an older GPS fix than the one already displayed. Drop it
          // instead of jumping the marker backward — unless this update is
          // only flipping live-stream presence (same/newer coords not required).
          const streamingChanged =
            existing &&
            Boolean(normalized.is_streaming) !== Boolean(existing.is_streaming);
          if (
            existing &&
            isStalePositionUpdate(normalized.timestamp, existing.timestamp) &&
            !streamingChanged
          ) {
            debugLog('cop', '[COPDashboard] ⏪ Dropping stale SSE update for', loc.device_id);
            return;
          }

          // Live Redis updates often omit course/entity fields; keep prior enrichment.
          const converted = existing
            ? {
                ...normalized,
                course_id: normalized.course_id ?? existing.course_id,
                entity_id: normalized.entity_id ?? existing.entity_id,
                entity_type: normalized.entity_type ?? existing.entity_type,
                device_type: normalized.device_type ?? existing.device_type,
                is_streaming: normalized.is_streaming ?? existing.is_streaming,
                phone_app_id: normalized.phone_app_id ?? existing.phone_app_id,
                wardriver_id: normalized.wardriver_id ?? existing.wardriver_id,
                watch_app_id: normalized.watch_app_id ?? existing.watch_app_id,
                vehicle_beacon_id: normalized.vehicle_beacon_id ?? existing.vehicle_beacon_id,
              }
            : normalized;

          // Always store raw device for re-filtering later
          sseRawDevicesRef.current.set(loc.device_id, converted);

          // Add to pending batch (will be flushed by timer)
          ssePendingUpdatesRef.current.set(loc.device_id, converted);
          setSseEventsReceived((n) => n + 1);

        } catch (err) {
          console.error('[COPDashboard] ❌ SSE Parse error:', err);
        }
      });

      eventSource.addEventListener('initial_sync_complete', () => {
        sseLastEventAtRef.current = Date.now();
        debugLog('cop', '[COPDashboard] ✅ SSE Initial sync complete');
        syncSseFilteredDevices(true);
      });

      // Handle heartbeat events (keeps connection alive)
      eventSource.addEventListener('heartbeat', (_e) => {
        sseLastEventAtRef.current = Date.now();
        debugLog('cop', '[COPDashboard] 💓 SSE Heartbeat received');
        // Don't log every heartbeat to console, just update last activity
      });

      // Track if we've already logged an SSE error (avoid spam)
      let sseErrorLogged = false;
      eventSource.onerror = (_e) => {
        setSseConnected(false);
        if (!sseErrorLogged) {
          sseErrorLogged = true;
          console.warn('[COPDashboard] SSE connection error - fetching devices via REST');
          copDataService.clearCache();
          loadDevicesRef.current?.();
        }
        debugLog('cop', '[COPDashboard] SSE Error - will auto-retry');
      };
    };

    connectSSE();

    // Watchdog: EventSource's built-in retry gives up permanently when the
    // server rejects the reconnect (e.g. 401 after the URL-embedded token
    // expired). Detect a CLOSED stream or prolonged silence and rebuild the
    // connection with a fresh token, so the map never stays frozen until a
    // manual page refresh.
    sseWatchdogTimerRef.current = setInterval(() => {
      if (!sseActiveRef.current) return;
      const es = eventSourceRef.current;
      const silentMs = Date.now() - sseLastEventAtRef.current;
      if (!es || es.readyState === EventSource.CLOSED || silentMs > SSE_SILENCE_THRESHOLD) {
        console.warn(
          `[COPDashboard] SSE watchdog reconnect (readyState=${es?.readyState ?? 'none'}, silent=${Math.round(silentMs / 1000)}s)`
        );
        setSseConnected(false);
        connectSSE();
      }
    }, SSE_WATCHDOG_INTERVAL);
    
    // DON'T close on cleanup in strict mode - use the ref to track
    return () => {
      debugLog('cop', '[COPDashboard] Effect cleanup (SSE stays open via ref)');
    };
  }, [useSSE, flushSSEUpdates, syncSseFilteredDevices]);

  useEffect(() => {
    loadDevicesRef.current = loadDevices;
  }, [loadDevices]);

  // Bootstrap from REST when SSE is active, then poll as a safety net for devices the stream missed.
  useEffect(() => {
    if (!useSSE) {
      return;
    }

    loadDevicesRef.current?.();

    const intervalId = setInterval(() => {
      copDataService.clearCache();
      loadDevicesRef.current?.();
    }, 10000);

    return () => clearInterval(intervalId);
  }, [useSSE]);
  
  // Mission overlay: snapshot first, then live. Its own EventSource rather than
  // a second listener on the location stream — a mission event is not a
  // location update, and the location stream is high-rate enough that mixing
  // the two would make either one harder to reason about.
  useEffect(() => {
    if (!intelEnabled) {
      setMissionRoutes([]);
      setMissionEvents([]);
      return;
    }

    let cancelled = false;
    let source: EventSource | null = null;

    const loadSnapshot = async () => {
      try {
        const snapshot = await missionEventsService.getOverlay();
        if (cancelled) return;
        setMissionRoutes(snapshot.missions);
        // Replace rather than merge: the snapshot is authoritative for the
        // window it covers, and anything newer arrives on the stream.
        setMissionEvents(snapshot.events);
      } catch (err) {
        debugLog('cop', '[COPDashboard] Mission overlay snapshot failed:', err);
      }
    };

    loadSnapshot();

    // The route itself changes only on a reroute or a status transition, and
    // neither is streamed, so a slow poll keeps the drawn line honest.
    const routeRefresh = setInterval(loadSnapshot, 60000);

    source = new EventSource(missionEventsService.streamUrl());
    source.addEventListener('mission_event', (e: MessageEvent) => {
      if (cancelled) return;
      try {
        const event: MissionEvent = JSON.parse(e.data);
        setMissionEvents((prev) =>
          prev.some((existing) => existing.id === event.id)
            ? prev
            : [event, ...prev].slice(0, 500),
        );
        if (event.event_type === 'route_changed') loadSnapshot();
      } catch (err) {
        console.error('[COPDashboard] Bad mission event payload:', err);
      }
    });
    source.onerror = () => {
      // EventSource reconnects on its own; the snapshot poll covers the gap.
      debugLog('cop', '[COPDashboard] Mission event stream interrupted');
    };

    return () => {
      cancelled = true;
      clearInterval(routeRefresh);
      source?.close();
    };
  }, [intelEnabled]);

  // Two ways a threat leaves the map, and they mean different things. An
  // acknowledgement is an operator saying "seen"; a stale event is the backend
  // saying nothing has observed it for a minute. Both stop the marker, and
  // conflating them would lose the difference between handled and gone.
  const missionThreats = useMemo(
    () => activeMissionThreats(missionEvents).filter((event) => !event.acknowledged_at),
    [missionEvents],
  );

  const handleMissionEventSelect = useCallback((event: MissionEvent) => {
    if (typeof event.latitude === 'number' && typeof event.longitude === 'number') {
      setMapViewState({ longitude: event.longitude, latitude: event.latitude, zoom: 15 });
      setUserHasInteractedWithMap(true);
    }
  }, [setMapViewState, setUserHasInteractedWithMap]);

  const handleAcknowledgeMissionEvent = useCallback(async (event: MissionEvent) => {
    // Optimistic: the row greys out immediately and the map marker drops. A
    // failed call is corrected by the next snapshot poll a minute later.
    const stamp = new Date().toISOString();
    setMissionEvents((prev) =>
      prev.map((item) => (item.id === event.id ? { ...item, acknowledged_at: stamp } : item)),
    );
    try {
      await missionEventsService.acknowledge(event.mission_id, event.id);
    } catch (err) {
      console.error('[COPDashboard] Failed to acknowledge mission event:', err);
    }
  }, []);

  const handlePromoteMissionThreat = useCallback(async () => {
    if (!missionPromoteTarget) return;
    setMissionPromoteSaving(true);
    setMissionPromoteError(null);
    try {
      const result = await missionEventsService.promoteToWatchlist(
        missionPromoteTarget.mission_id,
        missionPromoteTarget.id,
        missionPromoteReason,
      );
      setMissionPromoteTarget(null);
      setMissionPromoteReason('');
      setMissionPromoteNote(
        result.already_present
          ? `${result.identifier} was already on the watchlist.`
          : `${result.identifier} added to the watchlist — the next movement will recognise it immediately.`,
      );
    } catch (err: any) {
      setMissionPromoteError(err?.message || 'Could not add this emitter to the watchlist.');
    } finally {
      setMissionPromoteSaving(false);
    }
  }, [missionPromoteTarget, missionPromoteReason]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      sseActiveRef.current = false;
      if (sseFlushTimerRef.current) {
        clearInterval(sseFlushTimerRef.current);
        sseFlushTimerRef.current = null;
      }
      if (sseWatchdogTimerRef.current) {
        clearInterval(sseWatchdogTimerRef.current);
        sseWatchdogTimerRef.current = null;
      }
      if (eventSourceRef.current) {
        debugLog('cop', '[COPDashboard] 🛑 Component unmount - closing SSE');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      copDataService.cancelAllRequests();
      copAlertsService.stopPolling();
    };
  }, []);

  // Re-filter SSE devices when filters change (only when SSE is active)
  useEffect(() => {
    if (!useSSE || sseRawDevicesRef.current.size === 0) {
      return;
    }

    syncSseFilteredDevices(true);
  }, [
    useSSE,
    selectedDate,
    preferredTimezone,
    selectedCourses,
    selectedDeviceTypes,
    selectedEntityTypes,
    selectedEntities,
    courseFilterMode,
    deviceTypeFilterMode,
    entityTypeFilterMode,
    entityFilterMode,
    syncSseFilteredDevices,
  ]);

  // Check for existing unacknowledged SOS on initial load
  useEffect(() => {
    const checkInitialSOS = async () => {
      try {
        const response = await copAlertsService.getRecentSOS();
        
        if (response.messages.length > 0) {
          // Only show unacknowledged messages
          const unacknowledgedMessages = response.messages.filter(msg => !msg.acknowledged);
          
          if (unacknowledgedMessages.length > 0) {
            const mostRecent = unacknowledgedMessages[0];
            setSOSAlertMessage(mostRecent);
            
            // Show on map only if not acknowledged
            if (mostRecent.latitude && mostRecent.longitude) {
              setSOSMessages([mostRecent]);
            }
          }
        } 
      } catch (error) {
        console.error('[COP] Error checking initial SOS:', error);
      }
    };
    
    checkInitialSOS();
  }, []); // Only run once on mount

  // Start SOS polling for real-time alerts (uses ref to avoid restarting interval on every ack)
  useEffect(() => {
    copAlertsService.startPolling((newMessages: SOSMessage[]) => {
      const acked = acknowledgedSOSIdsRef.current;
      const unacknowledged = newMessages.filter(msg =>
        !msg.acknowledged && !acked.has(msg.id) && !sosAckInFlightRef.current.has(msg.id)
      );

      if (unacknowledged.length > 0) {
        const newAlert = unacknowledged[0];

        setSOSAlertMessage(newAlert);

        if (newAlert.latitude && newAlert.longitude) {
          setSOSMessages([newAlert]);
        }
      }
    }, 10000);

    return () => {
      copAlertsService.stopPolling();
    };
  }, []);

  // Convert the COP top-bar date dropdown into the lookback window the DFP
  // alerts endpoint expects. The endpoint takes an integer ``hours`` value and
  // applies it as ``detected_at >= NOW() - INTERVAL '<hours> hours'``.
  // Reset stale persisted filter values that are no longer offered in the
  // dropdown ("All Time" and "Last Week" pulled too much data and were
  // removed). Users who had those selected pre-update get bumped back to
  // the safe 1h default the next time they load the COP.
  React.useEffect(() => {
    if (selectedDate === 'All Time' || selectedDate === 'Last Week') {
      setSelectedDate('Last Hour');
    }
  }, [selectedDate, setSelectedDate]);

  // Specific YYYY-MM-DD dates are translated into "today minus that many days"
  // so the user sees alerts at least back to the chosen day.
  const dfpHoursWindow = React.useMemo(() => {
    const sd = (selectedDate || '').trim();
    switch (sd) {
      case 'Last Hour': return 1;
      case 'Last 8 Hours': return 8;
      case 'Last 12 Hours': return 12;
      case 'Last 24 Hours': return 24;
      case 'Last 48 Hours': return 48;
      case 'Last 72 Hours': return 72;
      // Defensive: handle persisted state from before these options were
      // removed. The mapper still translates them so the data layer doesn't
      // break, but the UI no longer offers them.
      case 'Last Week': return 24 * 7;
      case 'All Time': return 24 * 365;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(sd)) {
      const picked = new Date(`${sd}T00:00:00Z`);
      const diffMs = Date.now() - picked.getTime();
      const hours = Math.max(24, Math.ceil(diffMs / (60 * 60 * 1000)) + 24);
      return Math.min(hours, 24 * 365);
    }
    return 1;
  }, [selectedDate]);

  // Pin itinerary to the first single-entity selection. Adding companion
  // entities (or Exercise Support) keeps the pin; clearing the pin subject
  // or returning to "All IDs" drops it.
  useEffect(() => {
    if (selectedEntities.length === 0) {
      setItineraryEntityId(null);
      return;
    }
    if (selectedEntities.length === 1) {
      setItineraryEntityId(selectedEntities[0]);
      return;
    }
    setItineraryEntityId((prev) =>
      prev && selectedEntities.includes(prev) ? prev : null
    );
  }, [selectedEntities]);

  // Load itinerary for the pinned entity using the user-configured window:
  // past look-back (default 2 h) … future look-ahead (default 6 h).
  // Submissions are fetched slightly broader so same-day route_run values still
  // match; route entries are then tight-filtered to the display window.
  useEffect(() => {
    if (!itineraryEntityId) {
      setItinerarySubmissions([]);
      setShowItineraryOnMap(false);
      return;
    }

    let cancelled = false;
    const nowMs = Date.now();
    const windowStartMs = nowMs - itineraryLookbackMinutes * 60 * 1000;
    const windowEndMs = nowMs + itineraryLookaheadHours * 60 * 60 * 1000;
    // The list API filters on submission.route_run_datetime (not stop arrive/
    // depart). A run scheduled later today would be dropped if we pass the
    // display window as end_date — even when a stop at 11:25 is only minutes
    // away. Fetch a wide day-scale range, then filter entries client-side.
    const apiStartMs = Math.min(windowStartMs, nowMs - 48 * 60 * 60 * 1000);
    const apiEndMs = Math.max(windowEndMs, nowMs + 7 * 24 * 60 * 60 * 1000);

    (async () => {
      try {
        const response = await listItinerarySubmissions({
          student_number: itineraryEntityId,
          start_date: new Date(apiStartMs).toISOString(),
          end_date: new Date(apiEndMs).toISOString(),
          page_size: 100,
        });
        if (cancelled) return;

        const entryOverlapsWindow = (arrive: string, depart: string) => {
          const a = new Date(arrive).getTime();
          const d = new Date(depart).getTime();
          if (Number.isFinite(a) && Number.isFinite(d)) {
            return a <= windowEndMs && d >= windowStartMs;
          }
          if (Number.isFinite(a)) {
            return a >= windowStartMs && a <= windowEndMs;
          }
          return false;
        };

        const filtered = (response.items || [])
          .map((sub) => ({
            ...sub,
            route_entries: (sub.route_entries || []).filter((e) =>
              entryOverlapsWindow(e.arrive_datetime, e.depart_datetime)
            ),
          }))
          .filter((sub) => (sub.route_entries || []).length > 0);

        setItinerarySubmissions(filtered);
      } catch (err) {
        console.error('[COP] Failed to load itinerary submissions:', err);
        if (!cancelled) {
          setItinerarySubmissions([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [itineraryEntityId, itineraryLookbackMinutes, itineraryLookaheadHours]);

  // Poll DFP signal anomaly alerts when the RF Signal Module is enabled.
  // Works for both gov and commercial deployments (controlled by module
  // settings) and now respects the COP top-bar date dropdown - changing the
  // dropdown restarts polling with a new lookback window.
  // Poll status is admin-configurable (default: open only).
  useEffect(() => {
    if (!rfEnabled || !dfpAlertsService) return;
    let cancelled = false;

    const start = async () => {
      let pollStatus: string = 'open';
      try {
        if (typeof dfpAlertsService.getCopAlertSettings === 'function') {
          const settings = await dfpAlertsService.getCopAlertSettings();
          if (settings?.cop_poll_status === 'unacknowledged' || settings?.cop_poll_status === 'open') {
            pollStatus = settings.cop_poll_status;
          }
        }
      } catch {
        pollStatus = 'open';
      }
      if (cancelled) return;
      dfpAlertsService.startPolling((alerts: CopDFPAlert[]) => {
        setDFPAlerts(alerts || []);
      }, 15000, dfpHoursWindow, pollStatus);
    };

    start();
    return () => {
      cancelled = true;
      try { dfpAlertsService.stopPolling(); } catch { /* noop */ }
    };
  }, [rfEnabled, dfpHoursWindow]);

  const handleRefresh = async () => {
    if (isRefreshing) return;
    
    try {
      setIsRefreshing(true);
      // Clear cache to force fresh data
      copDataService.clearCache();
      
      // Load all data in parallel
      await Promise.all([
        loadDevices(),
        loadGeofences()
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleGeofenceDrawn = useCallback((geojson: any) => {
    setPendingGeofenceGeoJSON(geojson);
    openCopTab('geofences'); // Switch to Geofence tab to show the name/save dialog
  }, []);

  const clearPendingGeofence = useCallback(() => {
    setPendingGeofenceGeoJSON(null);
  }, []);

  const handleDeviceListSelect = useCallback((device: DeviceLocationStatus) => {
    const currentSelected = useDeviceStore.getState().selectedDevice;
    if (currentSelected?.device_id === device.device_id) {
      setSelectedDevice(null);
    } else {
      setSelectedDevice(device);
    }
  }, [setSelectedDevice]);

  const handleFollowToggle = useCallback((device: DeviceLocationStatus) => {
    const current = useDeviceStore.getState().followedDevice;
    if (current?.device_id === device.device_id) {
      setFollowedDevice(null);
      return;
    }
    setFollowedDevice(device);
    setSelectedDevice(device);
  }, [setFollowedDevice, setSelectedDevice]);

  const handleStatusEntryClick = useCallback((entries: any[]) => {
    setSelectedStatusEntries(entries);
  }, []);

  const handleSOSAcknowledge = useCallback(async (messageId: number) => {
    if (sosAckInFlightRef.current.has(messageId)) return;
    sosAckInFlightRef.current.add(messageId);

    // Optimistically dismiss immediately so polling doesn't re-trigger the modal
    setAcknowledgedSOSIds(prev => new Set(prev).add(messageId));
    setSOSAlertMessage(null);
    setSOSMessages([]);
    setSOSAckError(null);

    const MAX_RETRIES = 3;
    let success = false;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        await copAlertsService.acknowledgeSOS(messageId);
        success = true;
        break;
      } catch (error) {
        console.error(`[COP] ❌ Acknowledge attempt ${attempt}/${MAX_RETRIES} failed:`, error);
        if (attempt < MAX_RETRIES) {
          await new Promise(r => setTimeout(r, 1000 * attempt));
        }
      }
    }

    sosAckInFlightRef.current.delete(messageId);

    if (!success) {
      setSOSAckError('Failed to acknowledge SOS alert — it may reappear. Check your connection and try again.');
    }
  }, []);

  const handleSOSViewOnMap = useCallback(() => {
    if (sosAlertMessage && sosAlertMessage.latitude && sosAlertMessage.longitude) {
      setSOSMessages([sosAlertMessage]);
    }
  }, [sosAlertMessage]);

  const handleSOSClick = useCallback((message: SOSMessage) => {
    if (message.latitude && message.longitude) {
      // Don't show acknowledged messages on map - just toggle off if clicking same one
      if (message.acknowledged) {
        // If clicking an acknowledged message, just clear any existing marker
        setSOSMessages([]);
        return;
      }
      
      // Toggle: if clicking the same SOS that's already shown, clear it
      setSOSMessages(prev => {
        if (prev.length > 0 && prev[0].id === message.id) {
          return []; // Clear from map
        }
        return [message]; // Show on map
      });
    } 
  }, []);

  // Get device type breakdown (for all devices) - unused helpers commented
  // const deviceTypes: string[] = Array.from(new Set(devices.map((d: DeviceLocationStatus) => d.device_type).filter(Boolean))) as string[];
  // const getDeviceTypeCount = (type: string | undefined) => {
  //   if (!type) return 0;
  //   return devices.filter((d: DeviceLocationStatus) => d.device_type === type && (d.last_seen_minutes_ago ?? 999) <= 30).length;
  // };

  // const getEntityTypeCount = (type: string | undefined) => {
  //   if (!type) return 0;
  //   return devices.filter((d: DeviceLocationStatus) => d.entity_type === type && (d.last_seen_minutes_ago ?? 999) <= 30).length;
  // };

  // Get device type breakdown for devices in map bounds. An empty bounds result is
  // valid; only fall back to all devices before the map has reported its bounds.
  const hasMapBounds = mapBounds !== null;
  const devicesToUse = hasMapBounds ? devicesInBounds : devices;
  const totalDevicesForCurrentFilters = devices.length;
  const activeDevicesForCurrentFilters = devices.filter((d: DeviceLocationStatus) => (d.last_seen_minutes_ago ?? 999) <= 30).length;
  // const deviceTypesInView: string[] = Array.from(new Set(devicesToUse.map((d: DeviceLocationStatus) => d.device_type).filter(Boolean))) as string[];
  // const entityTypesInView: string[] = Array.from(new Set(devicesToUse.map((d: DeviceLocationStatus) => d.entity_type).filter(Boolean))) as string[];
  
  // const getDeviceTypeCountInView = (type: string | undefined) => {
  //   if (!type) return 0;
  //   return devicesToUse.filter((d: DeviceLocationStatus) => d.device_type === type && (d.last_seen_minutes_ago ?? 999) <= 30).length;
  // };
  
  // const getEntityTypeCountInView = (type: string | undefined) => {
  //   if (!type) return 0;
  //   return devicesToUse.filter((d: DeviceLocationStatus) => d.entity_type === type && (d.last_seen_minutes_ago ?? 999) <= 30).length;
  // };
  
  const totalDevicesInView = devicesToUse.length;
  const activeDevicesInView = devicesToUse.filter((d: DeviceLocationStatus) => (d.last_seen_minutes_ago ?? 999) <= 30).length;

  const cotravelerFollowedDevices = useMemo(
    () => devicesFollowedByCotraveler(devices, rfEnabled ? dfpAlerts : []),
    [devices, dfpAlerts, rfEnabled],
  );
  const cotravelerFollowedIds = useMemo(
    () => new Set(cotravelerFollowedDevices.map((d) => d.device_id)),
    [cotravelerFollowedDevices],
  );
  const cotravelerTickerText = useMemo(
    () => formatCotravelerTickerText(cotravelerFollowedDevices),
    [cotravelerFollowedDevices],
  );

  // Transform devices for LiveStatusDashboard
  const liveStatusDevices = useMemo(() => {
    return devices.map((d: DeviceLocationStatus) => ({
      id: d.device_id,
      entityId: d.entity_id || d.device_id,
      entityType: (d.entity_type?.trim() || (isCommercialBanner ? 'Unassigned' : 'Student')) as 'Student' | 'RP' | 'SRP' | string,
      // device_type was already normalized at the SSE/REST entry points to
      // a canonical capitalized form ("Phone" | "Watch" | "Vehicle" | ...).
      // Lowercase it here only because LiveStatusDashboard's row icon picker
      // expects 'phone' | 'watch' | 'vehicle'.
      deviceType: (d.device_type?.toLowerCase() as 'phone' | 'watch' | 'vehicle') || 'phone',
      lastSeen: new Date(),
      lastSeenMinutesAgo: d.last_seen_minutes_ago ?? 999999,
      isOnline: (d.last_seen_minutes_ago ?? 999) <= 30,
      signalStrength: undefined,
    }));
  }, [devices, isCommercialBanner]);

  // Filter devices for map based on status filter
  const filteredDevicesForMap = useMemo(() => {
    if (!statusFilter) return devices;
    
    return devices.filter((d: DeviceLocationStatus) => {
      const mins = d.last_seen_minutes_ago ?? 999999;
      switch (statusFilter) {
        case 'nrt': return mins < 1;
        case 'recent': return mins >= 1 && mins < 5;
        case 'reporting': return mins >= 5 && mins < 240; // 4 hours
        case 'idle': return mins >= 240 && mins < 1440; // 24 hours  
        case 'stale': return mins >= 1440 && mins < 4320; // 72 hours
        case 'offline': return mins >= 4320;
        default: return true;
      }
    });
  }, [devices, statusFilter]);

  return (
    <Box sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default',
      overflow: 'hidden'
    }}>
      {/* Compact Header - Responsive */}
      <Box sx={{ 
        flexShrink: 0,
        bgcolor: topBarChrome.headerBg,
        borderBottom: 1,
        borderColor: 'divider',
        px: { xs: 1, sm: 2 },
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        gap: 1
      }}>
        {/* Left: Title and Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: topBarChrome.fg, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            COP
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>
            {formatPreferredClockTime(lastUpdated)}
          </Typography>
        </Box>

        {/* Center: Filters - Collapsible on mobile */}
        <Box sx={{ 
          display: { xs: 'none', md: 'flex' }, 
          gap: 1, 
          alignItems: 'center',
          flex: 1,
          justifyContent: 'center'
        }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                // Clear timeline cache when date filter changes
                copDataService.clearCacheEntry('timeline-activity', {
                  selectedDate,
                  selectedCourses,
                  selectedDeviceTypes,
                  selectedEntityTypes,
                  selectedEntities
                });
              }}
              sx={{ 
                ...topBarChrome.selectSx,
                height: 32,
              }}
            >
              <MenuItem value="Last Hour">Last Hour</MenuItem>
              <MenuItem value="Last 8 Hours">Last 8 Hours</MenuItem>
              <MenuItem value="Last 12 Hours">Last 12 Hours</MenuItem>
              <MenuItem value="Last 24 Hours">Last 24 Hours</MenuItem>
              <MenuItem value="Last 48 Hours">Last 48 Hours</MenuItem>
              <MenuItem value="Last 72 Hours">Last 72 Hours</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              multiple
              value={selectedCourses}
              onChange={(e: SelectChangeEvent<string[]>) => {
                setSelectedCourses(e.target.value as string[]);
                copDataService.clearCacheEntry('timeline-activity', {
                  selectedDate,
                  selectedCourses,
                  selectedDeviceTypes,
                  selectedEntityTypes,
                  selectedEntities
                });
              }}
              displayEmpty
              renderValue={(selected) => {
                if (selected.length === 0) return allLabel;
                if (selected.length === availableCourses.length) return allLabel;
                if (selected.length === 1) return selected[0];
                return `${selected.length} ${coursePlural}`;
              }}
              sx={{ 
                ...topBarChrome.selectSx,
                height: 32,
                fontSize: '0.8rem',
              }}
              MenuProps={{
                PaperProps: {
                  sx: { '& .MuiMenuItem-root': { fontSize: '0.75rem', py: 0.5, minHeight: 28 } }
                }
              }}
            >
              {availableCourses.map((course) => (
                <MenuItem 
                  key={course} 
                  value={course}
                  sx={{ 
                    bgcolor: selectedCourses.includes(course) ? 'primary.dark' : 'transparent',
                    '&:hover': { bgcolor: selectedCourses.includes(course) ? 'primary.main' : 'action.hover' }
                  }}
                >
                  {course}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              multiple
              value={selectedDeviceTypes}
              onChange={(e: SelectChangeEvent<string[]>) => {
                setSelectedDeviceTypes(e.target.value as string[]);
                // Clear timeline cache when device type filter changes
                copDataService.clearCacheEntry('timeline-activity', {
                  selectedDate,
                  selectedCourses,
                  selectedDeviceTypes,
                  selectedEntityTypes,
                  selectedEntities
                });
              }}
              displayEmpty
              renderValue={(selected) => {
                if (selected.length === 0 || selected.length === availableDeviceTypes.length) return 'All Types';
                return selected.join(', ');
              }}
              sx={{ 
                ...topBarChrome.selectSx,
                height: 32,
                fontSize: '0.8rem',
              }}
              MenuProps={{
                PaperProps: {
                  sx: { '& .MuiMenuItem-root': { fontSize: '0.75rem', py: 0.5, minHeight: 28 } }
                }
              }}
            >
              {availableDeviceTypes.map((type) => (
                <MenuItem 
                  key={type} 
                  value={type}
                  sx={{ 
                    bgcolor: selectedDeviceTypes.includes(type) ? 'primary.dark' : 'transparent',
                    '&:hover': { bgcolor: selectedDeviceTypes.includes(type) ? 'primary.main' : 'action.hover' }
                  }}
                >
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              multiple
              value={selectedEntityTypes}
              onChange={(e: SelectChangeEvent<string[]>) => {
                setSelectedEntityTypes(e.target.value as string[]);
                // Clear timeline cache when entity type filter changes
                copDataService.clearCacheEntry('timeline-activity', {
                  selectedDate,
                  selectedCourses,
                  selectedDeviceTypes,
                  selectedEntityTypes,
                  selectedEntities
                });
              }}
              displayEmpty
              renderValue={(selected) => {
                if (selected.length === 0 || selected.length === availableEntityTypes.length) return 'All Entities';
                return selected.join(', ');
              }}
              sx={{ 
                ...topBarChrome.selectSx,
                height: 32,
                fontSize: '0.8rem',
              }}
              MenuProps={{
                PaperProps: {
                  sx: { '& .MuiMenuItem-root': { fontSize: '0.75rem', py: 0.5, minHeight: 28 } }
                }
              }}
            >
              {availableEntityTypes.map((type) => (
                <MenuItem 
                  key={type} 
                  value={type}
                  sx={{ 
                    bgcolor: selectedEntityTypes.includes(type) ? 'primary.dark' : 'transparent',
                    '&:hover': { bgcolor: selectedEntityTypes.includes(type) ? 'primary.main' : 'action.hover' }
                  }}
                >
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Autocomplete
            multiple
            size="small"
            value={selectedEntities}
            onChange={(_event, newValue) => {
              setSelectedEntities(newValue);
              // Clear timeline cache when entity filter changes
              copDataService.clearCacheEntry('timeline-activity', {
                selectedDate,
                selectedCourses,
                selectedDeviceTypes,
                selectedEntityTypes,
                selectedEntities: newValue
              });
            }}
            options={availableEntities}
            disableCloseOnSelect
            renderTags={() => null}
            ListboxProps={{
              sx: {
                '& .MuiAutocomplete-option': {
                  fontSize: '0.75rem',
                  py: 0.25,
                  minHeight: 28
                },
                '& .MuiAutocomplete-option[aria-selected="true"]': {
                  bgcolor: 'primary.dark !important'
                },
                '& .MuiAutocomplete-option[aria-selected="true"]:hover': {
                  bgcolor: 'primary.main !important'
                }
              }
            }}
            renderOption={(props, option) => (
              <li {...props} style={{ fontSize: '0.75rem', padding: '4px 12px' }}>
                {option}
              </li>
            )}
            renderInput={(params) => (
              <TextField 
                {...params} 
                placeholder={selectedEntities.length === 0 ? "All IDs" : `${selectedEntities.length} selected`}
                sx={{
                  '& .MuiInputBase-root': {
                    color: topBarChrome.fg,
                    height: 32,
                    py: 0,
                    fontSize: '0.8rem',
                    flexWrap: 'nowrap',
                    overflow: 'hidden'
                  },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                  '& .MuiSvgIcon-root': { color: topBarChrome.icon }
                }}
              />
            )}
            sx={{ minWidth: 140 }}
          />
        </Box>

        {/* Right: Filter Toggle (mobile) + Refresh + SSE */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Mobile Filter Toggle */}
          {isMobile && (
            <IconButton
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              sx={{ color: topBarChrome.icon, p: 0.5 }}
              size="small"
            >
              <FilterIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton 
            onClick={handleRefresh}
            disabled={isRefreshing}
            size="small"
            sx={{ color: topBarChrome.icon, p: 0.5 }}
          >
            {isRefreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon fontSize="small" />}
          </IconButton>
          {/* SSE Real-time Toggle */}
          <Chip 
            label={useSSE ? (sseConnected ? 'Live Stream ●' : 'Live Stream ○') : 'Poll'}
            size="small"
            onClick={() => setUseSSE(!useSSE)}
            sx={{ 
              bgcolor: useSSE ? (sseConnected ? '#4caf50' : '#ff9800') : '#666',
              color: 'white',
              fontWeight: 'bold',
              height: 22,
              cursor: 'pointer',
              '& .MuiChip-label': { px: 1, fontSize: '0.7rem' },
              '&:hover': { opacity: 0.9 }
            }}
            title={useSSE 
              ? `Live Stream ${sseConnected ? 'Connected' : 'Disconnected'} - ${sseStatus.eventsReceived} events` 
              : 'Click to enable real-time Live Stream updates'}
          />
        </Box>
      </Box>

      {/* Mobile Filters - Collapsible */}
      {isMobile && (
        <Collapse in={filtersExpanded}>
          <Box sx={{ 
            bgcolor: topBarChrome.headerBg,
            borderBottom: 1,
            borderColor: 'divider',
            px: 1,
            py: 1,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1
          }}>
            <FormControl size="small" sx={{ minWidth: 100, flex: '1 1 45%' }}>
              <Select
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                sx={{ 
                  ...topBarChrome.selectSx,
                  height: 32,
                  fontSize: '0.75rem',
                }}
              >
                <MenuItem value="Last Hour">Last Hour</MenuItem>
                <MenuItem value="Last 8 Hours">Last 8 Hours</MenuItem>
                <MenuItem value="Last 24 Hours">Last 24 Hours</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 100, flex: '1 1 45%' }}>
              <Select
                multiple
                value={selectedCourses}
                onChange={(e: SelectChangeEvent<string[]>) => setSelectedCourses(e.target.value as string[])}
                displayEmpty
                renderValue={(selected) => {
                  if (selected.length === 0) return allLabel;
                  if (selected.length === availableCourses.length) return allLabel;
                  if (selected.length === 1) return selected[0];
                  return `${selected.length} ${coursePlural}`;
                }}
                sx={{ 
                  ...topBarChrome.selectSx,
                  height: 32,
                  fontSize: '0.75rem',
                }}
                MenuProps={{
                  PaperProps: {
                    sx: { '& .MuiMenuItem-root': { fontSize: '0.75rem', py: 0.5, minHeight: 28 } }
                  }
                }}
              >
                {availableCourses.map((course) => (
                  <MenuItem 
                    key={course} 
                    value={course}
                    sx={{ 
                      bgcolor: selectedCourses.includes(course) ? 'primary.dark' : 'transparent',
                      '&:hover': { bgcolor: selectedCourses.includes(course) ? 'primary.main' : 'action.hover' }
                    }}
                  >
                    {course}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 100, flex: '1 1 45%' }}>
              <Select
                multiple
                value={selectedDeviceTypes}
                onChange={(e: SelectChangeEvent<string[]>) => setSelectedDeviceTypes(e.target.value as string[])}
                displayEmpty
                renderValue={(selected) => {
                  if (selected.length === 0 || selected.length === availableDeviceTypes.length) return 'All Types';
                  return selected.join(', ');
                }}
                sx={{ 
                  ...topBarChrome.selectSx,
                  height: 32,
                  fontSize: '0.75rem',
                }}
                MenuProps={{
                  PaperProps: {
                    sx: { '& .MuiMenuItem-root': { fontSize: '0.75rem', py: 0.5, minHeight: 28 } }
                  }
                }}
              >
                {availableDeviceTypes.map((type) => (
                  <MenuItem 
                    key={type} 
                    value={type}
                    sx={{ 
                      bgcolor: selectedDeviceTypes.includes(type) ? 'primary.dark' : 'transparent',
                      '&:hover': { bgcolor: selectedDeviceTypes.includes(type) ? 'primary.main' : 'action.hover' }
                    }}
                  >
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 100, flex: '1 1 45%' }}>
              <Select
                multiple
                value={selectedEntityTypes}
                onChange={(e: SelectChangeEvent<string[]>) => setSelectedEntityTypes(e.target.value as string[])}
                displayEmpty
                renderValue={(selected) => {
                  if (selected.length === 0 || selected.length === availableEntityTypes.length) return 'All Entities';
                  return selected.join(', ');
                }}
                sx={{ 
                  ...topBarChrome.selectSx,
                  height: 32,
                  fontSize: '0.75rem',
                }}
                MenuProps={{
                  PaperProps: {
                    sx: { '& .MuiMenuItem-root': { fontSize: '0.75rem', py: 0.5, minHeight: 28 } }
                  }
                }}
              >
                {availableEntityTypes.map((type) => (
                  <MenuItem 
                    key={type} 
                    value={type}
                    sx={{ 
                      bgcolor: selectedEntityTypes.includes(type) ? 'primary.dark' : 'transparent',
                      '&:hover': { bgcolor: selectedEntityTypes.includes(type) ? 'primary.main' : 'action.hover' }
                    }}
                  >
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              multiple
              size="small"
              value={selectedEntities}
              onChange={(_event, newValue) => {
                setSelectedEntities(newValue);
                copDataService.clearCacheEntry('timeline-activity', {
                  selectedDate,
                  selectedCourses,
                  selectedDeviceTypes,
                  selectedEntityTypes,
                  selectedEntities: newValue
                });
              }}
              options={availableEntities}
              disableCloseOnSelect
              renderTags={() => null}
              ListboxProps={{
                sx: {
                  '& .MuiAutocomplete-option': {
                    fontSize: '0.75rem',
                    py: 0.25,
                    minHeight: 28
                  },
                  '& .MuiAutocomplete-option[aria-selected="true"]': {
                    bgcolor: 'primary.dark !important'
                  },
                  '& .MuiAutocomplete-option[aria-selected="true"]:hover': {
                    bgcolor: 'primary.main !important'
                  }
                }
              }}
              renderOption={(props, option) => (
                <li {...props} style={{ fontSize: '0.75rem', padding: '4px 12px' }}>
                  {option}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={selectedEntities.length === 0 ? 'All IDs' : `${selectedEntities.length} selected`}
                  sx={{
                    '& .MuiInputBase-root': {
                      color: topBarChrome.fg,
                      height: 32,
                      py: 0,
                      fontSize: '0.75rem',
                      flexWrap: 'nowrap',
                      overflow: 'hidden'
                    },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                    '& .MuiSvgIcon-root': { color: topBarChrome.icon }
                  }}
                />
              )}
              sx={{ minWidth: 100, flex: '1 1 100%' }}
            />
          </Box>
        </Collapse>
      )}

{/* Scrolling Stats Ticker - Toggleable */}
      <Box sx={{
        flexShrink: 0,
        bgcolor: 'rgba(0,0,0,0.3)',
        borderBottom: 1,
        borderColor: 'divider',
        overflow: 'hidden',
        position: 'relative',
        height: showTicker ? 28 : 20,
        display: 'flex',
        alignItems: 'center',
        transition: 'height 0.2s ease',
      }}>
        {/* Toggle Button */}
        <Tooltip title={showTicker ? 'Hide ticker' : 'Show ticker'}>
          <IconButton
            size="small"
            onClick={() => setShowTicker(!showTicker)}
            sx={{
              position: 'absolute',
              left: 4,
              zIndex: 1,
              width: 20,
              height: 20,
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
            }}
          >
            {showTicker ? (
              <Typography sx={{ fontSize: '0.6rem', color: 'white' }}>−</Typography>
            ) : (
              <Typography sx={{ fontSize: '0.6rem', color: 'white' }}>+</Typography>
            )}
          </IconButton>
        </Tooltip>
        
        {showTicker ? (
          <>
            <style>
              {`
                @keyframes ticker {
                  0% { transform: translateX(100%); }
                  100% { transform: translateX(-100%); }
                }
              `}
            </style>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              whiteSpace: 'nowrap',
              animation: 'ticker 90s linear infinite',
              ml: 3,
              '&:hover': {
                animationPlayState: 'paused',
              },
            }}>
              {(() => {
                const recentlyActive = devices.filter((d: DeviceLocationStatus) => (d.last_seen_minutes_ago ?? 999) <= 5).length;
                const timeStr = formatPreferredClockTime(lastUpdated) || 'N/A';

                const dfpCriticalCount = dfpAlerts.filter(a => (a.severity || '').toLowerCase() === 'critical').length;
                const dfpWarningCount = dfpAlerts.filter(a => (a.severity || '').toLowerCase() === 'warning').length;
                const dfpColor = dfpCriticalCount > 0 ? '#f44336' : dfpWarningCount > 0 ? '#ff9800' : '#9e9e9e';

                // Entity-type breakdown for the ticker.
                // Gov side hardcodes Student / RP / SRP (stable taxonomy).
                // Commercial builds the breakdown dynamically from whatever entity
                // types are actually in the device list (Operator, Manpack, ...).
                const entityBreakdown: { icon: React.ReactNode; text: string; color: string }[] = [];
                if (isCommercialBanner) {
                  // Device-type chips (Phone/Watch/Vehicle) come first so the
                  // hardware breakdown is visible even when the preset hasn't
                  // populated entity-type names yet (everything bucketed as
                  // "Unassigned"). Device-type coloring is universal — green=
                  // phone, blue=watch, purple=vehicle — and matches the pins.
                  const phoneCountAll = devices.filter(d => (d.device_type || '').toLowerCase().includes('phone')).length;
                  const watchCountAll = devices.filter(d => {
                    const t = (d.device_type || '').toLowerCase();
                    return t.includes('watch') && !t.includes('wardriver');
                  }).length;
                  const vehicleCountAll = devices.filter(d => (d.device_type || '').toLowerCase().includes('vehicle')).length;
                  entityBreakdown.push(
                    { icon: <PhoneIphone sx={{ fontSize: 14 }} />, text: `Phone: ${phoneCountAll}`, color: '#4caf50' },
                    { icon: <WatchIcon sx={{ fontSize: 14 }} />, text: `Watch: ${watchCountAll}`, color: '#2196f3' },
                    { icon: <DirectionsCar sx={{ fontSize: 14 }} />, text: `Vehicle: ${vehicleCountAll}`, color: '#9c27b0' },
                  );
                  // Per-entity-type chips follow (Operator, Manpack, Unassigned,
                  // ...). These use the palette colors that drive list/filter
                  // chips, intentionally distinct from device-type colors.
                  entityTypeBreakdown.forEach(({ type, count, color }) => {
                    entityBreakdown.push({
                      icon: <Person sx={{ fontSize: 14 }} />,
                      text: `${type}: ${count}`,
                      color,
                    });
                  });
                } else {
                  // Split the Student total into per-device-type chips so the
                  // ticker carries the same color code as the map pins /
                  // legend / device list. A student device whose device_type
                  // isn't one of the three recognized variants falls into
                  // "Other" so the totals still reconcile.
                  const studentDevices = devices.filter((d: DeviceLocationStatus) => d.entity_type === 'Student');
                  const phoneCount = studentDevices.filter(d => (d.device_type || '').toLowerCase().includes('phone')).length;
                  const watchCount = studentDevices.filter(d => {
                    const t = (d.device_type || '').toLowerCase();
                    return t.includes('watch') && !t.includes('wardriver');
                  }).length;
                  const vehicleCount = studentDevices.filter(d => (d.device_type || '').toLowerCase().includes('vehicle')).length;
                  const otherStudentCount = studentDevices.length - phoneCount - watchCount - vehicleCount;
                  const rpCount = devices.filter((d: DeviceLocationStatus) => d.entity_type === 'RP').length;
                  const srpCount = devices.filter((d: DeviceLocationStatus) => d.entity_type === 'SRP').length;
                  entityBreakdown.push(
                    { icon: <PhoneIphone sx={{ fontSize: 14 }} />, text: `Phone: ${phoneCount}`, color: '#4caf50' },
                    { icon: <WatchIcon sx={{ fontSize: 14 }} />, text: `Watch: ${watchCount}`, color: '#2196f3' },
                    { icon: <DirectionsCar sx={{ fontSize: 14 }} />, text: `Vehicle: ${vehicleCount}`, color: '#9c27b0' },
                  );
                  // Only surface "Other" when there are unclassified student
                  // devices, to avoid cluttering the ticker with a 0 chip.
                  if (otherStudentCount > 0) {
                    entityBreakdown.push({
                      icon: <School sx={{ fontSize: 14 }} />,
                      text: `Other ${plural}: ${otherStudentCount}`,
                      color: '#4caf50',
                    });
                  }
                  entityBreakdown.push(
                    { icon: <Person sx={{ fontSize: 14 }} />, text: `RP: ${rpCount}`, color: '#ff9800' },
                    { icon: <Shield sx={{ fontSize: 14 }} />, text: `SRP: ${srpCount}`, color: '#f44336' },
                  );
                }

                const tickerItems: { icon: React.ReactNode; text: string; color: string }[] = [
                  { icon: <BarChart sx={{ fontSize: 14 }} />, text: `Total Devices: ${totalDevices}`, color: '#64B5F6' },
                  { icon: <CheckCircle sx={{ fontSize: 14 }} />, text: `Active (30min): ${activeDevices}`, color: '#81C784' },
                  { icon: <Bolt sx={{ fontSize: 14 }} />, text: `Recently Active (5min): ${recentlyActive}`, color: '#FFD54F' },
                  ...entityBreakdown,
                  { icon: <LocationOn sx={{ fontSize: 14 }} />, text: `In View: ${totalDevicesInView}`, color: '#90CAF9' },
                  ...(rfEnabled ? [{
                    icon: <AlertIcon sx={{ fontSize: 14 }} />,
                    text: `DFP Alerts: ${dfpAlerts.length}${dfpCriticalCount > 0 ? ` (${dfpCriticalCount} crit)` : dfpWarningCount > 0 ? ` (${dfpWarningCount} warn)` : ''}`,
                    color: dfpColor,
                  }] : []),
                  ...(rfEnabled && cotravelerTickerText ? [{
                    icon: <PeopleIcon sx={{ fontSize: 14 }} />,
                    text: cotravelerTickerText,
                    color: COTRAVELER_PULSE_COLOR,
                  }] : []),
                  { icon: <Sync sx={{ fontSize: 14 }} />, text: `Last Update: ${timeStr}`, color: '#B0BEC5' },
                  ...(useSSE && sseConnected ? [{ icon: <SettingsInputAntenna sx={{ fontSize: 14 }} />, text: `Live Stream Events: ${sseStatus.eventsReceived}`, color: '#4caf50' }] : []),
                ];
                
                // Duplicate items for seamless loop
                const allItems = [...tickerItems, ...tickerItems];
                
                return allItems.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mx: 3,
                      px: 1.5,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: 'rgba(255,255,255,0.05)',
                    }}
                  >
                    <span style={{ fontSize: '0.8rem' }}>{item.icon}</span>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        color: item.color,
                        letterSpacing: '0.02em',
                      }}
                    >
                      {item.text}
                    </Typography>
                  </Box>
                ));
              })()}
            </Box>
          </>
        ) : (
          <Typography sx={{ 
            fontSize: '0.65rem', 
            color: 'rgba(255,255,255,0.5)', 
            ml: 4,
            fontStyle: 'italic',
          }}>
            Stats ticker hidden
          </Typography>
        )}
      </Box>

      {/* Main Content - Map + Right Panel (stacked on mobile) */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        overflow: 'hidden', 
        p: { xs: 0.5, md: 1 }, 
        gap: { xs: 0.5, md: 1 }
      }}>
        {/* Map Container - Top on mobile, size adjusts based on panel state */}
        <Box sx={{ 
          flex: { 
            xs: mobilePanelSize === 'minimized' ? '1 1 auto' : 
                mobilePanelSize === 'half' ? '0 0 55%' : '0 0 20%', 
            md: 1 
          },
          minHeight: { 
            xs: mobilePanelSize === 'minimized' ? 'calc(100% - 48px)' : 
                mobilePanelSize === 'half' ? '50vh' : '15vh', 
            md: 'auto' 
          },
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          position: 'relative',
          transition: 'flex 0.3s ease, min-height 0.3s ease'
        }}>
<COPMapGL
            devices={filteredDevicesForMap}
            selectedDevice={selectedDevice}
            onDeviceSelect={(device) => setSelectedDevice(device)}
            onFollowDevice={(device) => setFollowedDevice(device)}
            geofences={geofences}
            selectedGeofenceId={selectedGeofenceId}
            mapViewState={mapViewState}
            isDrawingMode={isDrawingGeofence}
            onGeofenceDrawn={handleGeofenceDrawn}
            followedDevice={followedDevice}
            onMapBoundsChange={setDevicesInBounds}
            onGeoBoundsChange={setMapBounds}
            onCameraLegendChange={setCameraLegend}
            statusEntries={selectedStatusEntries}
            sosMessages={sosMessages}
            selectedDwell={selectedDwell}
            sidebarOpen={rightPanelOpen}
            isMobile={isMobile}
            mobilePanelSize={mobilePanelSize}
            aiMarkers={aiMarkers}
            dfpAlerts={rfEnabled ? dfpAlerts : []}
            selectedDfpAlert={rfEnabled ? selectedDfpAlert : null}
            dfpEmitter={rfEnabled ? dfpEmitter : null}
            trafficIncidents={trafficIncidents}
            weatherAlerts={weatherAlerts}
            areaAlertsLoading={areaAlertsLoading}
            onLoadAreaAlerts={loadAreaAlerts}
            onAreaAlertsViewportChange={setAreaAlertViewport}
            watchLiveDeviceId={watchLiveDeviceId}
            onWatchLiveDeviceIdChange={setWatchLiveDeviceId}
            isDrawingAnalysisArea={isDrawingAnalysisArea}
            analysisAreaGeometry={analysisAreaGeometry}
            entityRoutes={entityRoutes}
            dwellCircles={dwellCircles}
            missionRoutes={missionRoutes}
            missionThreats={missionThreats}
            onMissionThreatSelect={handleMissionEventSelect}
            itinerarySubmissions={itinerarySubmissions}
            showItinerary={showItineraryOnMap}
            onShowItineraryChange={setShowItineraryOnMap}
            itineraryToggleEnabled={!!itineraryEntityId}
            itineraryLookbackMinutes={itineraryLookbackMinutes}
            itineraryLookaheadHours={itineraryLookaheadHours}
            onItineraryWindowChange={handleItineraryWindowChange}
            onAnalysisAreaDrawn={(geometry) => {
              setAnalysisAreaGeometry(geometry);
              setIsDrawingAnalysisArea(false);
              debugLog('cop', '[COP] Analysis area drawn:', geometry);
            }}
            onAnalysisAreaCleared={() => {
              setAnalysisAreaGeometry(null);
              setIsDrawingAnalysisArea(false);
              debugLog('cop', '[COP] Analysis area cleared from map toolbar');
            }}
            onUserInteraction={() => {
              if (selectedBookmark === 'Auto' && !userHasInteractedWithMap) {
                setUserHasInteractedWithMap(true);
              }
              if (useDeviceStore.getState().followedDevice) {
                setFollowedDevice(null);
              }
            }}
          />

          {followedDevice && (
            <Chip
              icon={<LocationOn sx={{ fontSize: '16px !important' }} />}
              label={`Following ${followedDevice.entity_id || followedDevice.device_id}`}
              onDelete={() => setFollowedDevice(null)}
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                zIndex: 5,
                bgcolor: 'rgba(33, 150, 243, 0.92)',
                color: '#fff',
                fontWeight: 700,
                '& .MuiChip-icon': { color: '#fff' },
                '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.9)' },
              }}
            />
          )}

          {/* Timeline Widget at bottom - hidden on small mobile */}
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <TimelineWidget
              selectedDate={selectedDate}
              timeZone={preferredTimezone}
              selectedCourse={selectedCourseForChild}
              selectedDeviceType={timelineDeviceType}
              selectedEntityType={timelineEntityType}
              selectedEntity={selectedEntities.length === 1 ? selectedEntities[0] : null}
              activeTabId={activeTabId}
              mapBounds={mapBounds}
              dfpAlerts={dfpAlerts}
              commercial={isCommercialBanner}
            />
          </Box>
        </Box>

        {/* Collapsible Panel - Right on desktop, Bottom on mobile with 3 size states */}
        <Box sx={{
          width: { xs: '100%', md: rightPanelOpen ? 500 : 40 },
          flex: { 
            xs: mobilePanelSize === 'minimized' ? '0 0 48px' : 
                mobilePanelSize === 'half' ? '0 0 45%' : '0 0 80%', 
            md: 'none' 
          },
          minHeight: { 
            xs: mobilePanelSize === 'minimized' ? 48 : 
                mobilePanelSize === 'half' ? '40vh' : '75vh', 
            md: 'auto' 
          },
          maxHeight: { xs: mobilePanelSize === 'expanded' ? '85vh' : 'none', md: 'none' },
          transition: 'all 0.3s ease',
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Toggle Button - Different behavior on mobile (cycles through 3 states) */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: { xs: 'center', md: rightPanelOpen ? 'flex-end' : 'center' },
            alignItems: 'center',
            p: 0.5,
            borderBottom: { xs: mobilePanelSize !== 'minimized' ? 1 : 0, md: rightPanelOpen ? 1 : 0 },
            borderColor: 'divider',
            cursor: 'pointer',
            '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' },
            // Drag handle appearance on mobile
            ...(isMobile && {
              '&::before': {
                content: '""',
                width: 40,
                height: 4,
                borderRadius: 2,
                bgcolor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
                position: 'absolute',
                top: 6
              }
            })
          }}
          onClick={() => isMobile ? cycleMobilePanelSize() : setRightPanelOpen(!rightPanelOpen)}
          >
            {/* Mobile: Show size indicator with label */}
            {isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, mt: 1, color: topBarChrome.fg }}>
                {mobilePanelSize === 'minimized' ? <KeyboardArrowUpIcon /> : 
                 mobilePanelSize === 'half' ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                  {mobilePanelSize === 'minimized' ? 'Tap to expand' : 
                   mobilePanelSize === 'half' ? 'Tap for full • Swipe down to hide' : 'Tap to minimize'}
                </Typography>
              </Box>
            )}
            {/* Desktop: Arrow icon only */}
            {!isMobile && (
              <IconButton 
                size="small"
                sx={{ color: topBarChrome.icon }}
              >
                {rightPanelOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            )}
          </Box>

          {/* Panel Content - Show when not minimized on mobile, or when open on desktop */}
          {((isMobile && mobilePanelSize !== 'minimized') || (!isMobile && rightPanelOpen)) && (
            <>
              {/* Tabs - More compact on mobile */}
              <Tabs 
                value={activeTab} 
                onChange={(_e, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ 
                  borderBottom: 1,
                  borderColor: 'divider',
                  minHeight: { xs: 36, md: 40 },
                  '& .MuiTab-root': {
                    minHeight: { xs: 36, md: 40 },
                    fontSize: { xs: '0.65rem', md: '0.75rem' },
                    color: 'text.secondary',
                    minWidth: { xs: 50, md: 60 },
                    padding: { xs: '4px 8px', md: '6px 12px' },
                    '&.Mui-selected': {
                      color: '#4caf50'
                    }
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#4caf50'
                  },
                  '& .MuiTabs-scrollButtons': {
                    color: topBarChrome.icon,
                    width: { xs: 24, md: 40 },
                    '&.Mui-disabled': {
                      opacity: 0.3
                    }
                  }
                }}
              >
                {visibleCopTabs.map((t) => {
                  if (t.id === 'stats') return <Tab key={t.id} icon={<DashboardIcon fontSize="small" />} label="Stats" />;
                  if (t.id === 'events') return <Tab key={t.id} icon={<AssessmentIcon fontSize="small" />} label="Events" />;
                  if (t.id === 'devices') return <Tab key={t.id} icon={<PeopleIcon fontSize="small" />} label="Devices" />;
                  if (t.id === 'status') return <Tab key={t.id} icon={<StatusIcon fontSize="small" />} label="Status" />;
                  if (t.id === 'legend') return <Tab key={t.id} icon={<MapIcon fontSize="small" />} label="Legend" />;
                  if (t.id === 'dwells') return <Tab key={t.id} icon={<DwellIcon fontSize="small" />} label="Dwells" />;
                  if (t.id === 'geofences') return <Tab key={t.id} icon={<GeofenceIcon fontSize="small" />} label="Geofences" />;
                  if (t.id === 'streams') {
                    return (
                      <Tab
                        key={t.id}
                        icon={
                          <Badge
                            badgeContent={liveStreamCount}
                            color="error"
                            max={99}
                            sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 16, minWidth: 16, right: -4, top: -2 } }}
                          >
                            <VideocamIcon fontSize="small" />
                          </Badge>
                        }
                        label="Streams"
                        sx={{
                          '& .MuiTab-iconWrapper': {
                            color: liveStreamCount > 0 ? '#ef4444' : 'inherit',
                          },
                        }}
                      />
                    );
                  }
                  if (t.id === 'alerts') {
                    return (
                      <Tab
                        key={t.id}
                        icon={
                          <Badge
                            badgeContent={sosMessages.length + areaAlertCount}
                            color="error"
                            max={99}
                            sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 16, minWidth: 16, right: -4, top: -2 } }}
                          >
                            <AlertIcon fontSize="small" />
                          </Badge>
                        }
                        label={`Alerts${areaAlertCount > 0 ? ` (${areaAlertCount})` : ''}`}
                        sx={{
                          '& .MuiTab-iconWrapper': {
                            color: sosMessages.length > 0 || areaAlertCount > 0 ? '#f44336' : 'inherit'
                          }
                        }}
                      />
                    );
                  }
                  if (t.id === 'chat') {
                    return (
                      <Tab
                        key={t.id}
                        icon={
                          <Badge badgeContent={chatUnread} color="error" max={99}
                            sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 16, minWidth: 16, right: -4, top: -2 } }}>
                            <ChatIcon fontSize="small" />
                          </Badge>
                        }
                        label="Chat"
                      />
                    );
                  }
                  if (t.id === 'dfp') return <Tab key={t.id} icon={<SettingsInputAntenna fontSize="small" />} label="DFP" />;
                  if (t.id === 'rf_feed') return <Tab key={t.id} icon={<Sync fontSize="small" />} label="RF Feed" />;
                  return null;
                })}
              </Tabs>

              {/* Tab Content - Responsive padding */}
              <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 1, md: 2 } }}>
                {/* Stats Tab */}
                {activeTabId === 'stats' && (
                  <Box>
                    {/* Live Status Dashboard */}
                    <Box sx={{ mb: 2 }}>
                      <LiveStatusDashboard
                        devices={liveStatusDevices}
                        onRefresh={handleRefresh}
                        isLoading={isRefreshing}
                        timeZone={preferredTimezone}
                        selectedStatusFilter={statusFilter}
                        onStatusFilterChange={setStatusFilter}
                        commercial={isCommercialBanner}
                        entityTypeColors={entityTypeBreakdown.reduce((acc: Record<string, string>, e) => {
                          acc[e.type] = e.color;
                          return acc;
                        }, {} as Record<string, string>)}
                      />
                    </Box>

                    {/* Map View Stats Card */}
                    <Paper sx={{ 
                      background: themeColors.cardBg,
                      border: `1px solid ${themeColors.border}`,
                      borderRadius: 3,
                      overflow: 'hidden',
                      backdropFilter: 'blur(10px)',
                      boxShadow: isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.08)',
                      mb: 2
                    }}>
                      {/* Header */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 2,
                        borderBottom: `1px solid ${themeColors.border}`,
                        background: themeColors.headerBg,
                      }}>
                        <Box sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 2,
                          background: isDark 
                            ? `linear-gradient(135deg, rgba(${themeColors.primaryRgb}, 0.2), rgba(${themeColors.primaryRgb}, 0.1))`
                            : `linear-gradient(135deg, rgba(${themeColors.primaryRgb}, 0.15), rgba(${themeColors.primaryRgb}, 0.08))`,
                          border: `1px solid ${themeColors.border}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <MapIcon sx={{ color: themeColors.accent, fontSize: 18 }} />
                        </Box>
                        <Box>
                          <Typography sx={{
                            color: themeColors.text,
                            fontWeight: 'bold',
                            fontSize: '0.85rem',
                            textShadow: isDark ? `0 0 10px ${themeColors.accentGlow}` : 'none',
                          }}>
                            Map View Statistics
                          </Typography>
                          <Typography sx={{ color: themeColors.textTertiary, fontSize: '0.65rem' }}>
                            Current map extent
                          </Typography>
                        </Box>
                      </Box>

                      {/* Stats Grid */}
                      <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
                        <Box sx={{ 
                          flex: 1, 
                          p: 1.5, 
                          borderRadius: 2,
                          background: themeColors.subtleBg,
                          border: `1px solid rgba(${themeColors.primaryRgb}, 0.15)`,
                          textAlign: 'center'
                        }}>
                          <Typography sx={{ color: themeColors.textSecondary, fontSize: '0.65rem', textTransform: 'uppercase', mb: 0.5 }}>
                            Total in View
                          </Typography>
                          <Typography sx={{ 
                            color: themeColors.accent, 
                            fontWeight: 'bold', 
                            fontSize: '1.8rem',
                            textShadow: isDark ? `0 0 20px ${themeColors.accentGlow}` : 'none',
                            lineHeight: 1
                          }}>
                            {totalDevicesInView}
                          </Typography>
                          <Typography sx={{ color: themeColors.textTertiary, fontSize: '0.6rem', mt: 0.5 }}>
                            of {totalDevicesForCurrentFilters} total
                          </Typography>
                        </Box>

                        <Box sx={{ 
                          flex: 1, 
                          p: 1.5, 
                          borderRadius: 2,
                          background: isDark ? 'rgba(76, 175, 80, 0.05)' : 'rgba(76, 175, 80, 0.08)',
                          border: '1px solid rgba(76, 175, 80, 0.15)',
                          textAlign: 'center'
                        }}>
                          <Typography sx={{ color: themeColors.textSecondary, fontSize: '0.65rem', textTransform: 'uppercase', mb: 0.5 }}>
                            Active in View
                          </Typography>
                          <Typography sx={{ 
                            color: '#4caf50', 
                            fontWeight: 'bold', 
                            fontSize: '1.8rem',
                            textShadow: isDark ? '0 0 20px rgba(76, 175, 80, 0.5)' : 'none',
                            lineHeight: 1
                          }}>
                            {activeDevicesInView}
                          </Typography>
                          <Typography sx={{ color: themeColors.textTertiary, fontSize: '0.6rem', mt: 0.5 }}>
                            of {activeDevicesForCurrentFilters} active
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>

                    {/* Entity breakdown now integrated in LiveStatusDashboard */}
                  </Box>
                )}

                {/* Devices Tab */}
                {activeTabId === 'devices' && (
                  <Box sx={{ height: '100%', overflow: 'hidden' }}>
                    <DeviceListPanel
                      timeZone={preferredTimezone}
                      devices={(() => {
                        const base = hasMapBounds ? devicesInBounds : devices;
                        if (!statusFilter) return base;
                        return base.filter((d: DeviceLocationStatus) => {
                          const mins = d.last_seen_minutes_ago ?? 999999;
                          switch (statusFilter) {
                            case 'nrt': return mins < 1;
                            case 'recent': return mins >= 1 && mins < 5;
                            case 'reporting': return mins >= 5 && mins < 240;
                            case 'idle': return mins >= 240 && mins < 1440;
                            case 'stale': return mins >= 1440 && mins < 4320;
                            case 'offline': return mins >= 4320;
                            default: return true;
                          }
                        });
                      })()}
                      selectedDevice={selectedDevice}
                      followedDevice={followedDevice}
                      cotravelerFollowedIds={cotravelerFollowedIds}
                      cotravelerBanner={cotravelerTickerText}
                      onDeviceSelect={handleDeviceListSelect}
                      onFollowToggle={handleFollowToggle}
                      statusFilterLabel={statusFilter ? { nrt: 'Real-Time', recent: 'Recent', reporting: 'Reporting', idle: 'Idle', stale: 'Stale', offline: 'Offline' }[statusFilter] : null}
                      statusFilterColor={statusFilter ? { nrt: '#4caf50', recent: '#8bc34a', reporting: '#ffeb3b', idle: '#ff9800', stale: '#ff5722', offline: '#f44336' }[statusFilter] : null}
                      onClearStatusFilter={() => setStatusFilter(null)}
                      onShareToChat={(device) => {
                        const entity = device.entity_id || device.device_id;
                        const loc = device.latitude && device.longitude
                          ? ` at ${device.latitude.toFixed(4)}, ${device.longitude.toFixed(4)}`
                          : '';
                        const speed = device.speed ? ` • ${formatSpeedMph(device.speed)}` : '';
                        const battery = device.battery_level != null ? ` • ${device.battery_level}% battery` : '';
                        copTeamChatService.sendMessage(
                          `Shared ${entity}${loc}${speed}${battery}`,
                          'general',
                          'alert'
                        ).catch(() => {});
                      }}
                    />
                  </Box>
                )}

                {/* Legend Tab */}
                {activeTabId === 'legend' && (
                  <Box>
                    {/* Entity Types Card */}
                    <Paper sx={{
                      background: themeColors.cardBg,
                      border: `1px solid ${themeColors.border}`,
                      borderRadius: 2,
                      overflow: 'hidden',
                      backdropFilter: 'blur(10px)',
                      boxShadow: isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.08)',
                      mb: 1,
                    }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.25,
                        py: 0.75,
                        borderBottom: `1px solid ${themeColors.border}`,
                        background: themeColors.headerBg,
                      }}>
                        <Box sx={{
                          width: 22,
                          height: 22,
                          borderRadius: 1.25,
                          background: isDark
                            ? `linear-gradient(135deg, rgba(${themeColors.primaryRgb}, 0.2), rgba(${themeColors.primaryRgb}, 0.1))`
                            : `linear-gradient(135deg, rgba(${themeColors.primaryRgb}, 0.15), rgba(${themeColors.primaryRgb}, 0.08))`,
                          border: `1px solid ${themeColors.border}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <PeopleIcon sx={{ color: themeColors.accent, fontSize: 13 }} />
                        </Box>
                        <Typography sx={{
                          color: themeColors.text,
                          fontWeight: 'bold',
                          fontSize: '0.8rem',
                        }}>
                          Entity Types
                        </Typography>
                      </Box>
                      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {(() => {
                          // Gov: training-taxonomy legend. Primary subjects are split
                          // by device type (phone/watch/vehicle) to match the Pathfinder
                          // map's per-device-type palette; RP and SRP keep their
                          // existing role colors.
                          // Commercial: emit one row per entity type actually present
                          // in the device list, deterministically colored from the
                          // shared palette.
                          const legendItems = isCommercialBanner
                            ? (entityTypeBreakdown.length > 0
                                ? entityTypeBreakdown.map(({ type, color, description }) => ({
                                    label: type,
                                    color,
                                    desc: description,
                                  }))
                                : [{ label: 'No entities reporting', color: '#9e9e9e', desc: '—' }])
                            : [
                                  { color: '#4caf50', label: 'Phone', desc: `${singular} phone` },
                                  { color: '#2196f3', label: 'Watch', desc: `${singular} watch` },
                                  { color: '#9c27b0', label: 'Vehicle', desc: `${singular} vehicle` },
                                  // Not a subject device type, so it gets its own
                                  // wording. Cyan mirrors COP_DEVICE_COLORS.drone.
                                  { color: '#00bcd4', label: 'Drone', desc: 'Aircraft (UAS)' },
                                { color: '#ff9800', label: 'RP', desc: 'Role Player' },
                                { color: '#f44336', label: 'SRP', desc: 'Surveillance Team' },
                              ];
                          return legendItems.map((item) => (
                            <Box key={item.label} sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              px: 0.75,
                              py: 0.5,
                              borderRadius: 1,
                              background: isDark ? `${item.color}12` : `${item.color}0d`,
                              border: `1px solid ${item.color}22`,
                            }}>
                              <Box sx={{
                                width: 12,
                                height: 12,
                                backgroundColor: item.color,
                                borderRadius: '50%',
                                flexShrink: 0,
                                boxShadow: isDark ? `0 0 6px ${item.color}50` : 'none',
                              }} />
                              <Typography sx={{ color: themeColors.text, fontWeight: 600, fontSize: '0.75rem' }}>
                                {item.label}
                              </Typography>
                              <Typography sx={{ color: themeColors.textTertiary, fontSize: '0.7rem', ml: 'auto' }}>
                                {item.desc}
                              </Typography>
                            </Box>
                          ));
                        })()}
                      </Box>
                    </Paper>

                    {/* Map Labels Card */}
                    <Paper sx={{
                      background: themeColors.cardBg,
                      border: `1px solid ${themeColors.border}`,
                      borderRadius: 2,
                      overflow: 'hidden',
                      backdropFilter: 'blur(10px)',
                      boxShadow: isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.08)',
                      mb: 1,
                    }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.25,
                        py: 0.75,
                        borderBottom: `1px solid ${themeColors.border}`,
                        background: themeColors.headerBg,
                      }}>
                        <Box sx={{
                          width: 22,
                          height: 22,
                          borderRadius: 1.25,
                          background: isDark
                            ? `linear-gradient(135deg, rgba(${themeColors.primaryRgb}, 0.2), rgba(${themeColors.primaryRgb}, 0.1))`
                            : `linear-gradient(135deg, rgba(${themeColors.primaryRgb}, 0.15), rgba(${themeColors.primaryRgb}, 0.08))`,
                          border: `1px solid ${themeColors.border}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <MapIcon sx={{ color: themeColors.accent, fontSize: 13 }} />
                        </Box>
                        <Typography sx={{
                          color: themeColors.text,
                          fontWeight: 'bold',
                          fontSize: '0.8rem',
                        }}>
                          Map Labels
                        </Typography>
                      </Box>
                      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {isCommercialBanner ? (
                          // Commercial: the pin labels are number-only and the
                          // color encodes the device type, exactly like gov.
                          // The colored example numbers teach that story; the
                          // separate "Entity Types" card carries the chip-color
                          // palette used by filters and lists.
                          <Box sx={{
                            px: 0.75, py: 0.5, borderRadius: 1, background: themeColors.subtleBg,
                            display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap',
                          }}>
                            <Typography component="span" sx={{ display: 'flex', gap: 0.75, fontWeight: 'bold', fontSize: '0.78rem' }}>
                              <Box component="span" sx={{ color: '#4caf50' }}>27</Box>
                              <Box component="span" sx={{ color: '#2196f3' }}>15</Box>
                              <Box component="span" sx={{ color: '#9c27b0' }}>02</Box>
                              <Box component="span" sx={{ color: '#00bcd4' }}>01</Box>
                            </Typography>
                            <Typography sx={{ color: themeColors.textSecondary, fontSize: '0.65rem', ml: 'auto' }}>
                              Pin color: green=Phone, blue=Watch, purple=Vehicle, cyan=Drone
                            </Typography>
                          </Box>
                        ) : (
                          <>
                            {/* Each example sits inline with its caption so
                                the legend stops eating vertical space — the
                                colored numbers themselves still teach the
                                color → device-type mapping. */}
                            <Box sx={{
                              px: 0.75, py: 0.5, borderRadius: 1, background: themeColors.subtleBg,
                              display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap',
                            }}>
                              <Typography component="span" sx={{ display: 'flex', gap: 0.75, fontWeight: 'bold', fontSize: '0.78rem' }}>
                                <Box component="span" sx={{ color: '#4caf50' }}>27</Box>
                                <Box component="span" sx={{ color: '#2196f3' }}>15</Box>
                                <Box component="span" sx={{ color: '#9c27b0' }}>02</Box>
                                <Box component="span" sx={{ color: '#00bcd4' }}>01</Box>
                              </Typography>
                              <Typography sx={{ color: themeColors.textSecondary, fontSize: '0.65rem', ml: 'auto' }}>
                                {`${plural}: green=Phone, blue=Watch, purple=Vehicle, cyan=Drone`}
                              </Typography>
                            </Box>
                            <Box sx={{
                              px: 0.75, py: 0.5, borderRadius: 1, background: themeColors.subtleBg,
                              display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap',
                            }}>
                              <Typography sx={{ color: '#ff9800', fontWeight: 'bold', fontSize: '0.78rem' }}>
                                51, 42, 33
                              </Typography>
                              <Typography sx={{ color: themeColors.textSecondary, fontSize: '0.65rem', ml: 'auto' }}>
                                RP personnel numbers
                              </Typography>
                            </Box>
                            <Box sx={{
                              px: 0.75, py: 0.5, borderRadius: 1, background: themeColors.subtleBg,
                              display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap',
                            }}>
                              <Typography sx={{ color: '#f44336', fontWeight: 'bold', fontSize: '0.78rem' }}>
                                20, 10, 05
                              </Typography>
                              <Typography sx={{ color: themeColors.textSecondary, fontSize: '0.65rem', ml: 'auto' }}>
                                SRP personnel numbers
                              </Typography>
                            </Box>
                          </>
                        )}
                      </Box>
                    </Paper>

                    {/* Camera Locations Card: shared camera overlay, per category, when the layer is on */}
                    {cameraLegend && (
                      <Paper sx={{
                        background: themeColors.cardBg,
                        border: `1px solid ${themeColors.border}`,
                        borderRadius: 2,
                        overflow: 'hidden',
                        backdropFilter: 'blur(10px)',
                        boxShadow: isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.08)',
                        mb: 1,
                      }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 1.25,
                          py: 0.75,
                          borderBottom: `1px solid ${themeColors.border}`,
                          background: themeColors.headerBg,
                        }}>
                          <Box sx={{
                            width: 22,
                            height: 22,
                            borderRadius: 1.25,
                            background: isDark
                              ? `linear-gradient(135deg, rgba(${themeColors.primaryRgb}, 0.2), rgba(${themeColors.primaryRgb}, 0.1))`
                              : `linear-gradient(135deg, rgba(${themeColors.primaryRgb}, 0.15), rgba(${themeColors.primaryRgb}, 0.08))`,
                            border: `1px solid ${themeColors.border}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            <CameraGlyphIcon sx={{ color: themeColors.accent, fontSize: 13 }} />
                          </Box>
                          <Typography sx={{
                            color: themeColors.text,
                            fontWeight: 'bold',
                            fontSize: '0.8rem',
                          }}>
                            Camera Locations
                          </Typography>
                          <Typography sx={{ color: themeColors.textSecondary, fontSize: '0.7rem', ml: 'auto' }}>
                            {cameraLegend.loading
                              ? 'loading…'
                              : cameraLegend.error
                                ? 'failed to load'
                                : `${cameraLegend.shownCount.toLocaleString()} shown`}
                          </Typography>
                        </Box>
                        <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {(cameraLegend.categories.length > 0
                            ? cameraLegend.categories
                            : [{ key: 'camera', label: 'Camera', color: '#00838f', count: cameraLegend.shownCount, visible: true }]
                          ).map((cat) => (
                            <Box key={cat.key} sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              px: 0.75,
                              py: 0.5,
                              borderRadius: 1,
                              borderLeft: `3px solid ${cat.color}`,
                              background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                              opacity: cat.visible ? 1 : 0.45,
                            }}>
                              <CameraGlyphIcon sx={{ color: cat.color, fontSize: 14, flexShrink: 0, filter: isDark ? `drop-shadow(0 0 3px ${cat.color})` : 'none' }} />
                              <Typography sx={{ color: themeColors.text, fontWeight: 600, fontSize: '0.75rem' }}>
                                {cat.label}
                              </Typography>
                              <Typography sx={{ color: themeColors.textSecondary, fontSize: '0.7rem', ml: 'auto' }}>
                                {cat.visible ? cat.count.toLocaleString() : 'hidden'}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Paper>
                    )}

                    {/* Reference Layers Card: admin foundational layers the user has turned on */}
                    {enabledReferenceLayers.length > 0 && (
                      <Paper sx={{
                        background: themeColors.cardBg,
                        border: `1px solid ${themeColors.border}`,
                        borderRadius: 2,
                        overflow: 'hidden',
                        backdropFilter: 'blur(10px)',
                        boxShadow: isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.08)',
                        mb: 1,
                      }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 1.25,
                          py: 0.75,
                          borderBottom: `1px solid ${themeColors.border}`,
                          background: themeColors.headerBg,
                        }}>
                          <Box sx={{
                            width: 22,
                            height: 22,
                            borderRadius: 1.25,
                            background: isDark
                              ? `linear-gradient(135deg, rgba(${themeColors.primaryRgb}, 0.2), rgba(${themeColors.primaryRgb}, 0.1))`
                              : `linear-gradient(135deg, rgba(${themeColors.primaryRgb}, 0.15), rgba(${themeColors.primaryRgb}, 0.08))`,
                            border: `1px solid ${themeColors.border}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            <LayersIcon sx={{ color: themeColors.accent, fontSize: 13 }} />
                          </Box>
                          <Typography sx={{
                            color: themeColors.text,
                            fontWeight: 'bold',
                            fontSize: '0.8rem',
                          }}>
                            Reference Layers
                          </Typography>
                          <Typography sx={{ color: themeColors.textSecondary, fontSize: '0.7rem', ml: 'auto' }}>
                            {enabledReferenceLayers.length} on
                          </Typography>
                        </Box>
                        <Box sx={{ px: 1, py: 0.5 }}>
                          <ReferenceLayersLegend
                            layers={enabledReferenceLayers}
                            textColor={themeColors.text}
                            secondaryColor={themeColors.textSecondary}
                          />
                        </Box>
                      </Paper>
                    )}

                    {/* Activity Status Card */}
                    <Paper sx={{
                      background: themeColors.cardBg,
                      border: `1px solid ${themeColors.border}`,
                      borderRadius: 2,
                      overflow: 'hidden',
                      backdropFilter: 'blur(10px)',
                      boxShadow: isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.08)',
                    }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.25,
                        py: 0.75,
                        borderBottom: `1px solid ${themeColors.border}`,
                        background: themeColors.headerBg,
                      }}>
                        <Box sx={{
                          width: 22,
                          height: 22,
                          borderRadius: 1.25,
                          background: isDark
                            ? `linear-gradient(135deg, rgba(${themeColors.primaryRgb}, 0.2), rgba(${themeColors.primaryRgb}, 0.1))`
                            : `linear-gradient(135deg, rgba(${themeColors.primaryRgb}, 0.15), rgba(${themeColors.primaryRgb}, 0.08))`,
                          border: `1px solid ${themeColors.border}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <StatusIcon sx={{ color: themeColors.accent, fontSize: 13 }} />
                        </Box>
                        <Typography sx={{
                          color: themeColors.text,
                          fontWeight: 'bold',
                          fontSize: '0.8rem',
                        }}>
                          Activity Status
                        </Typography>
                      </Box>
                      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {[
                          { opacity: 1, label: 'Active', desc: '< 30 minutes', color: '#4caf50' },
                          { opacity: 0.7, label: 'Recent', desc: '< 24 hours', color: '#ff9800' },
                          { opacity: 0.4, label: 'Offline', desc: '> 24 hours', color: '#f44336' },
                        ].map((item) => (
                          <Box key={item.label} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 0.75,
                            py: 0.5,
                            borderRadius: 1,
                            borderLeft: `3px solid ${item.color}`,
                            background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                          }}>
                            <Box sx={{
                              width: 12,
                              height: 12,
                              backgroundColor: item.color,
                              borderRadius: '50%',
                              flexShrink: 0,
                              opacity: item.opacity,
                              boxShadow: isDark ? `0 0 5px ${item.color}` : 'none',
                            }} />
                            <Typography sx={{ color: themeColors.text, fontWeight: 600, fontSize: '0.75rem' }}>
                              {item.label}
                            </Typography>
                            <Typography sx={{ color: themeColors.textSecondary, fontSize: '0.7rem', ml: 'auto' }}>
                              {item.desc}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Paper>

                    {/* Camera Coverage Card — the drone layers had no legend at
                        all, which left the translucent cyan shapes and the
                        dashed edge unexplained. Toggled from the map's Layers
                        control. */}
                    <Paper sx={{
                      background: themeColors.cardBg,
                      border: `1px solid ${themeColors.border}`,
                      borderRadius: 2,
                      overflow: 'hidden',
                      backdropFilter: 'blur(10px)',
                      boxShadow: isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.08)',
                      mt: 1,
                    }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.25,
                        py: 0.75,
                        borderBottom: `1px solid ${themeColors.border}`,
                        background: themeColors.headerBg,
                      }}>
                        <Box sx={{
                          width: 22,
                          height: 22,
                          borderRadius: 1.25,
                          background: isDark
                            ? `linear-gradient(135deg, rgba(${themeColors.primaryRgb}, 0.2), rgba(${themeColors.primaryRgb}, 0.1))`
                            : `linear-gradient(135deg, rgba(${themeColors.primaryRgb}, 0.15), rgba(${themeColors.primaryRgb}, 0.08))`,
                          border: `1px solid ${themeColors.border}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <MapIcon sx={{ color: themeColors.accent, fontSize: 13 }} />
                        </Box>
                        <Typography sx={{
                          color: themeColors.text,
                          fontWeight: 'bold',
                          fontSize: '0.8rem',
                        }}>
                          Camera Coverage
                        </Typography>
                      </Box>
                      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {[
                          {
                            label: 'Camera footprint',
                            desc: 'Ground the aircraft camera sees now',
                            sx: {
                              background: 'rgba(0, 188, 212, 0.22)',
                              border: '1.5px solid #00bcd4',
                            },
                          },
                          {
                            label: 'Range cutoff',
                            desc: 'Dashed edge is a distance limit, not the view end',
                            sx: {
                              background: 'transparent',
                              border: '1.5px dashed #00bcd4',
                            },
                          },
                          {
                            label: 'Area observed',
                            desc: 'Accumulated ground, selected aircraft, last 2 h',
                            sx: {
                              background: 'rgba(0, 188, 212, 0.12)',
                              border: '1px solid rgba(0, 188, 212, 0.5)',
                            },
                          },
                          {
                            label: 'Phone view cone',
                            desc: 'Indicative only — heading, no altitude or gimbal',
                            sx: {
                              background: 'rgba(239, 68, 68, 0.22)',
                              border: '1.5px solid #f87171',
                            },
                          },
                        ].map((item) => (
                          <Box key={item.label} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 0.75,
                            py: 0.5,
                            borderRadius: 1,
                            background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                          }}>
                            <Box sx={{
                              width: 18,
                              height: 12,
                              borderRadius: 0.5,
                              flexShrink: 0,
                              ...item.sx,
                            }} />
                            <Typography sx={{ color: themeColors.text, fontWeight: 600, fontSize: '0.75rem' }}>
                              {item.label}
                            </Typography>
                            <Typography sx={{ color: themeColors.textTertiary, fontSize: '0.7rem', ml: 'auto', textAlign: 'right' }}>
                              {item.desc}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Paper>
                  </Box>
                )}

                {/* Events Tab - Smart Analytics */}
                {activeTabId === 'events' && (
                  <Box sx={{ 
                    height: { xs: '100%', md: 'calc(100vh - 280px)' }, 
                    minHeight: { xs: 200, md: 400 },
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <SmartAnalytics
                      devices={devices}
                      totalDevices={totalDevices}
                      activeDevices={activeDevices}
                      lastUpdated={lastUpdated}
                      sseConnected={sseConnected}
                      filters={{
                        course: selectedCourseForChild,
                        entityType: selectedEntityTypes.length === 1 ? selectedEntityTypes[0] : 'All',
                        entity: selectedEntities.length === 1 ? selectedEntities[0] : 'All',
                      }}
                      currentTab="Events"
                      devicesInView={totalDevicesInView}
                      dwells={dwellsData}
                      geofences={geofences.map(g => ({
                        id: g.id,
                        name: g.name || g.description || `Geofence ${g.id}`,
                        type: g.type || 'standard',
                        is_active: g.is_active !== false,
                      }))}
                      onMapAction={(action) => {
                        // Handle map actions from AI chat
                        if (action.latitude !== undefined && action.longitude !== undefined) {
                          // Mark as user interaction so Auto-zoom doesn't override
                          setUserHasInteractedWithMap(true);
                          setMapViewState({
                            latitude: action.latitude,
                            longitude: action.longitude,
                            zoom: action.zoom || 14,
                          });
                        }
                        // If filtering by entity type, update the filter
                        if (action.type === 'filter_entity_type' && action.entityType) {
                          setSelectedEntityTypes([action.entityType]);
                        }
                        // Handle AI-generated markers
                        if (action.type === 'add_markers' && action.markers) {
                          setAiMarkers(action.markers as AIMapMarker[]);
                          debugLog('cop', '[COP] Added AI markers:', action.markers.length);
                        }
                        if (action.type === 'clear_markers') {
                          clearAiMarkers();
                          debugLog('cop', '[COP] Cleared AI markers');
                        }
                        // Handle area analysis actions
                        if (action.type === 'start_area_analysis') {
                          setIsDrawingAnalysisArea(true);
                          setAnalysisAreaGeometry(null);
                          debugLog('cop', '[COP] Started area analysis drawing mode');
                        }
                        if (action.type === 'cancel_area_analysis') {
                          setIsDrawingAnalysisArea(false);
                          setAnalysisAreaGeometry(null);
                          debugLog('cop', '[COP] Cancelled area analysis');
                        }
                        // Handle entity dwell actions
                        if (action.type === 'show_entity_dwells' && (action as any).dwellCircles) {
                          const circles = (action as any).dwellCircles;
                          debugLog('cop', `[COP] Received show_entity_dwells action with ${circles.length} circles:`, circles.slice(0, 2));
                          setDwellCircles(circles);
                          // Mark as user interaction so Auto-zoom doesn't override
                          setUserHasInteractedWithMap(true);
                          // Also zoom to the dwells
                          if (action.latitude !== undefined && action.longitude !== undefined) {
                            setMapViewState({
                              latitude: action.latitude,
                              longitude: action.longitude,
                              zoom: action.zoom || 12,
                            });
                            debugLog('cop', `[COP] Zooming to dwells at (${action.latitude}, ${action.longitude})`);
                          }
                          debugLog('cop', `[COP] Set dwellCircles state with ${circles.length} circles`);
                        }
                        if (action.type === 'clear_dwells') {
                          setDwellCircles([]);
                          debugLog('cop', '[COP] Cleared dwell circles');
                        }
                        // Handle entity route actions
                        if (action.type === 'show_entity_route' && action.routes) {
                          setEntityRoutes(action.routes as any);
                          // Mark as user interaction so Auto-zoom doesn't override
                          setUserHasInteractedWithMap(true);
                          // Also zoom to the route
                          if (action.latitude !== undefined && action.longitude !== undefined) {
                            setMapViewState({
                              latitude: action.latitude,
                              longitude: action.longitude,
                              zoom: action.zoom || 12,
                            });
                            debugLog('cop', `[COP] Zooming to route at (${action.latitude}, ${action.longitude})`);
                          }
                          debugLog('cop', '[COP] Added entity routes:', action.routes.length);
                        }
                        if (action.type === 'clear_routes') {
                          setEntityRoutes([]);
                          debugLog('cop', '[COP] Cleared entity routes');
                        }
                        // Clear ALL AI-added elements at once
                        if (action.type === 'clear_all_ai') {
                          clearAiMarkers();
                          setEntityRoutes([]);
                          setDwellCircles([]);
                          setAnalysisAreaGeometry(null);
                          setIsDrawingAnalysisArea(false);
                          debugLog('cop', '[COP] Cleared all AI-added elements (markers, routes, dwells, area)');
                        }
                      }}
                      mapViewport={mapViewState || undefined}
                      analysisArea={analysisAreaGeometry}
                      isDrawingAnalysisArea={isDrawingAnalysisArea}
                    />
                  </Box>
                )}

                {/* Geofences Tab */}
                {activeTabId === 'streams' && (
                  <LiveStreamsTab
                    devices={devices}
                    isActive={activeTabId === 'streams'}
                    selectedDeviceId={selectedDevice?.device_id || null}
                    onLocateDevice={handleLocateStreamingDevice}
                    onWatchOnMap={handleWatchStreamingOnMap}
                  />
                )}

                {activeTabId === 'geofences' && (
                  <GeofenceTab
                    onStartDrawing={() => setIsDrawingGeofence(true)}
                    onStopDrawing={() => setIsDrawingGeofence(false)}
                    isDrawing={isDrawingGeofence}
                    onGeofenceSelect={setSelectedGeofenceId}
                    selectedGeofenceId={selectedGeofenceId}
                    geofences={geofences}
                    mapBounds={mapBounds}
                    onGeofenceDrawn={pendingGeofenceGeoJSON}
                    onClearPendingGeofence={clearPendingGeofence}
                    onGeofenceCreated={() => loadGeofences(true)}
                    onGeofenceDeleted={() => loadGeofences(true)}
                    onGeofenceZoom={(lat, lng) => {
                      setMapViewState({
                        longitude: lng,
                        latitude: lat,
                        zoom: 15  // Good zoom level for geofence view
                      });
                      setUserHasInteractedWithMap(true);
                    }}
                    dfpAlerts={rfEnabled ? dfpAlerts : []}
                    activityHoursWindow={dfpHoursWindow}
                    activityWindowLabel={selectedDate || 'Last Hour'}
                  />
                )}

                {/* Dwells Tab */}
                {activeTabId === 'dwells' && (
                  <DwellTab
                    selectedCourse={selectedCourseForChild}
                    selectedDeviceType={timelineDeviceType || 'All'}
                    selectedEntityType={timelineEntityType || 'All'}
                    selectedEntity={selectedEntities.length === 1 ? selectedEntities[0] : undefined}
                    selectedDate={selectedDate}
                    timeZone={preferredTimezone}
                    onDwellSelect={(dwell) => {
                      debugLog('cop', '[COPDashboard] Dwell selected:', dwell);
                      
                      if (!dwell) {
                        // Dwell deselected - restore previous map view
                        if (previousMapView) {
                          setMapViewState(previousMapView);
                          setPreviousMapView(null);
                        }
                        setSelectedDwell(null);
                      } else if (dwell.latitude && dwell.longitude) {
                        // Save current view if it exists (to restore later)
                        if (mapViewState) {
                          setPreviousMapView({
                            longitude: mapViewState.longitude,
                            latitude: mapViewState.latitude,
                            zoom: mapViewState.zoom
                          });
                        }
                        // Zoom to dwell location
                        setMapViewState({
                          longitude: dwell.longitude,
                          latitude: dwell.latitude,
                          zoom: 16  // Close zoom for dwell location
                        });
                        setUserHasInteractedWithMap(true);
                        // Set selected dwell for map marker
                        setSelectedDwell({
                          latitude: dwell.latitude,
                          longitude: dwell.longitude,
                          entity_id: dwell.entity_id,
                          duration: dwell.duration,
                          start_time: dwell.start_time
                        });
                      }
                    }}
                  />
                )}

                {/* Status Tab */}
                {activeTabId === 'status' && (
                  <StatusTab
                    selectedCourse={selectedCourseForChild}
                    selectedDeviceType={timelineDeviceType || undefined}
                    selectedEntityType={timelineEntityType || undefined}
                    selectedEntity={selectedEntities.length === 1 ? selectedEntities[0] : undefined}
                    devicesInBounds={hasMapBounds ? devicesInBounds.map((d: DeviceLocationStatus) => d.device_id) : undefined}
                    onStatusEntryClick={handleStatusEntryClick}
                  />
                )}

                {/* Alerts Tab */}
                {activeTabId === 'alerts' && (
                  <AlertsTab
                    selectedCourse={selectedCourseForChild}
                    selectedDeviceType={timelineDeviceType || undefined}
                    selectedEntityType={timelineEntityType || undefined}
                    selectedEntity={selectedEntities.length === 1 ? selectedEntities[0] : undefined}
                    onSOSClick={handleSOSClick}
                    trafficIncidents={trafficIncidents}
                    weatherAlerts={weatherAlerts}
                    areaAlertsLoading={areaAlertsLoading}
                    areaAlertsError={areaAlertsError}
                    areaAlertsLoadedAt={areaAlertsLoadedAt}
                    areaAlertsStale={areaAlertsStale}
                    areaAlertsViewport={areaAlertViewport}
                    onLoadAreaAlerts={loadAreaAlerts}
                    onAreaAlertClick={handleAreaAlertClick}
                    missionsEnabled={intelEnabled}
                    missionRoutes={missionRoutes}
                    missionEvents={missionEvents}
                    onMissionEventClick={handleMissionEventSelect}
                    onAcknowledgeMissionEvent={handleAcknowledgeMissionEvent}
                    onPromoteMissionThreat={setMissionPromoteTarget}
                  />
                )}

                {/* Team Chat Tab */}
                {activeTabId === 'chat' && (
                  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    {isCommercialBanner && (
                      <Tabs
                        value={chatSubTab}
                        onChange={(_, v) => setChatSubTab(v)}
                        variant="fullWidth"
                        sx={{ minHeight: 36, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
                      >
                        <Tab value="chat" label="Chat" sx={{ minHeight: 36, py: 0 }} />
                        <Tab value="ptt" label="PTT" sx={{ minHeight: 36, py: 0 }} />
                      </Tabs>
                    )}
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        display: !isCommercialBanner || chatSubTab === 'chat' ? 'flex' : 'none',
                        flexDirection: 'column',
                      }}
                    >
                      <TeamChatTab
                        currentUserEmail={authUser?.email}
                        currentUserName={authUser?.name}
                        selectedCourse={selectedCourseForChild}
                        isVisible={activeTabId === 'chat' && (!isCommercialBanner || chatSubTab === 'chat')}
                        onUnreadChange={setChatUnread}
                        onZoomToLocation={(lat, lng) => {
                          setMapViewState({ latitude: lat, longitude: lng, zoom: 16 });
                          setUserHasInteractedWithMap(true);
                        }}
                      />
                    </Box>
                    {isCommercialBanner && (
                      <Box sx={{ flex: 1, minHeight: 0, display: chatSubTab === 'ptt' ? 'flex' : 'none', flexDirection: 'column' }}>
                        <PttCaptionFeed
                          courseId={selectedCourseForChild !== 'All Courses' ? selectedCourseForChild : undefined}
                          onZoomToLocation={(lat, lng) => {
                            // Keep the view from before the first PTT zoom so deselect returns there,
                            // even when the user hops between several transmissions.
                            if (!prePttZoomView) {
                              const live = areaAlertViewport;
                              if (live) {
                                setPrePttZoomView({
                                  latitude: (live.bounds.north + live.bounds.south) / 2,
                                  longitude: (live.bounds.east + live.bounds.west) / 2,
                                  zoom: live.zoom,
                                });
                              } else if (mapViewState) {
                                setPrePttZoomView({ ...mapViewState });
                              }
                            }
                            setMapViewState({ latitude: lat, longitude: lng, zoom: 17 });
                            setUserHasInteractedWithMap(true);
                          }}
                          onClearZoom={() => {
                            if (prePttZoomView) {
                              setMapViewState(prePttZoomView);
                              setPrePttZoomView(null);
                            }
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                )}

                {/* DFP Tab (RF module) */}
                {activeTabId === 'dfp' && DFPAlertsTab && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary">View:</Typography>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 999,
                          overflow: 'hidden',
                        }}
                      >
                        {([
                          ['all', 'All'],
                          ['actionable', 'Alerts'],
                          ['insights', 'Insights'],
                        ] as Array<['actionable' | 'insights' | 'all', string]>).map(([val, lbl], idx, list) => {
                          const active = dfpCategory === val;
                          return (
                            <Button
                              key={val}
                              size="small"
                              onClick={() => setDfpCategory(val)}
                              sx={{
                                minWidth: 64,
                                px: 1.5,
                                py: 0.2,
                                fontSize: '0.7rem',
                                fontWeight: active ? 700 : 500,
                                textTransform: 'none',
                                borderRadius: 0,
                                borderRight: idx < list.length - 1 ? '1px solid' : 'none',
                                borderColor: 'divider',
                                bgcolor: active
                                  ? (val === 'actionable' ? 'error.main' : 'primary.main')
                                  : 'transparent',
                                color: active ? 'primary.contrastText' : 'text.secondary',
                                '&:hover': {
                                  bgcolor: active
                                    ? (val === 'actionable' ? 'error.dark' : 'primary.dark')
                                    : 'action.hover',
                                },
                              }}
                            >
                              {lbl}
                            </Button>
                          );
                        })}
                      </Box>
                    </Box>
                    <DFPAlertsTab
                    category={dfpCategory}
                    hours={dfpHoursWindow}
                    selectedDeviceIds={
                      selectedEntities.length > 0
                        ? devices
                            .filter(d => selectedEntities.includes(d.entity_id || ''))
                            .map(d => d.device_id)
                        : undefined
                    }
                    deviceIdToEntity={devices.reduce((acc: Record<string, string>, d) => {
                        if (d.device_id) acc[d.device_id] = d.entity_id || d.device_id;
                        return acc;
                      }, {} as Record<string, string>)}
                    onEmitterLocated={(loc: any) => setDfpEmitter(loc)}
                    onAlertClick={(alert: CopDFPAlert, expanding: boolean) => {
                      if (!alert.latitude || !alert.longitude) return;
                      if (!expanding) setDfpEmitter(null);
                      if (expanding) {
                        if (mapViewState) {
                          setPreDfpAlertView({
                            longitude: mapViewState.longitude,
                            latitude: mapViewState.latitude,
                            zoom: mapViewState.zoom,
                          });
                        }
                        setSelectedDfpAlert(alert);
                        setMapViewState({
                          latitude: alert.latitude,
                          longitude: alert.longitude,
                          zoom: 15,
                        });
                        setUserHasInteractedWithMap(true);
                      } else {
                        setSelectedDfpAlert(null);
                        if (preDfpAlertView) {
                          setMapViewState(preDfpAlertView);
                          setPreDfpAlertView(null);
                        }
                      }
                    }}
                  />
                  </Box>
                )}
                {/* RF Feed Tab (RF module) — shows all MQTT collectors;
                    Map toggle (off by default) can clip to map extent. */}
                {activeTabId === 'rf_feed' && SignalWaterfallTab && (
                  <SignalWaterfallTab
                    mapBounds={mapBounds}
                    deviceIdToEntity={devices.reduce((acc: Record<string, string>, d) => {
                        if (d.device_id) acc[d.device_id] = d.entity_id || d.device_id;
                        return acc;
                      }, {} as Record<string, string>)}
                    onZoomToLocation={(lat, lng) => {
                      setMapViewState({ latitude: lat, longitude: lng, zoom: 16 });
                      setUserHasInteractedWithMap(true);
                    }}
                  />
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* SOS Alert Modal - appears globally over everything */}
      <SOSAlertModal
        message={sosAlertMessage}
        onAcknowledge={handleSOSAcknowledge}
        onViewOnMap={handleSOSViewOnMap}
      />
      <Snackbar
        open={!!sosAckError}
        autoHideDuration={8000}
        onClose={() => setSOSAckError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        message={sosAckError}
        ContentProps={{ sx: { bgcolor: '#d32f2f', fontWeight: 'bold' } }}
      />

      {/* Promote a confirmed route threat onto the DFP watchlist. Identifier
          inference happens server-side; this only collects the justification. */}
      <Dialog
        open={!!missionPromoteTarget}
        onClose={() => !missionPromoteSaving && setMissionPromoteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem' }}>Add to watchlist</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {missionPromoteTarget?.label || 'This emitter'} will be flagged on sight for every
            future movement, not just this one.
          </Typography>
          {missionPromoteError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>{missionPromoteError}</Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Reason"
            placeholder="What made this a threat?"
            value={missionPromoteReason}
            onChange={(e) => setMissionPromoteReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMissionPromoteTarget(null)}
            disabled={missionPromoteSaving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handlePromoteMissionThreat}
            disabled={missionPromoteSaving}
          >
            {missionPromoteSaving ? 'Adding…' : 'Add to watchlist'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!missionPromoteNote}
        autoHideDuration={6000}
        onClose={() => setMissionPromoteNote(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        message={missionPromoteNote}
      />
    </Box>
  );
};

export default COPDashboardClean;

